// /quotes/:id editor. Loads the quote + the active form schema,
// renders one SchemaForm per line, and offers Save (PATCH) and
// Get pricing (POST /:id/price). Pricing snapshot from the loaded
// quote renders without any extra round-trip; the "Get pricing"
// button triggers a fresh ERP call and updates the block.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import i18n from 'i18next';
import { Trans, useTranslation } from 'react-i18next';

import type {
  AssemblySnapshot,
  ComponentRole,
  FormSchemaDto,
  PricingFailureDto,
  PricingResponseDto,
  QuoteDto,
  QuoteStatusDto,
} from 'shared';
import {
  BOOKLET_PRODUCT_TYPES,
  FINISHING_CONFIG_KEY,
  ASSEMBLY_FIELD_PREFIX,
  ASSEMBLY_ITEM_PRODUCT_TYPE,
  ORDER_SERVICE_FIELD_LABELS,
  SHIP_TO_FIELD_PREFIX,
  SHRINKWRAP_CONFIG_KEY,
  SHRINKWRAP_FAMILY_KEY,
  SPECIALTY_FINISHING_CONFIG_KEY,
  SPECIALTY_FINISHING_KEYS,
  DOOR_PRESELECTED_PRODUCT_TYPE_KEY,
  isChangeOrderEditableStatus,
  isSalesQuoteOpenForRep,
  isConfiguredLine,
  isEntryDivision,
  quoteHasProofableLine,
  kitContentRowIncomplete,
  ASSEMBLY_ITEM_CONFIG_KEY,
  assemblyItemIsPerUnit,
  assemblyItemSwitchCrossesPerUnit,
  lineConfigWithFreshAssemblyIds,
  withAssemblyItemSnapshot,
  withAssemblyItemUnitTotal,
  defaultNeedByDate,
  fieldKeyLabel,
  LINE_TITLE_FIELD_KEY,
  LINE_TITLE_FIELD_LABEL,
  lineIssueAnchorKey,
  parseLineIssueKey,
  MEDIA_CONTENT_LINK_CONFIG_KEY,
  readMediaContentDelivery,
  turnaroundDaysForProductTypes,
  lineConfigIssues,
  lineMissingUploadFieldKeys,
  lineNeedsVariableDataList,
  linePendableArtworkRoles,
  linePendingArtworkRoles,
  customPackagingCarriesArt,
  mediaPackagingCarriesArt,
  quoteArtworkPending,
  withArtworkPendingRole,
  withoutLandedArtworkMarks,
  ARTWORK_PENDING_ROLES_CONFIG_KEY,
  fieldScope,
  groupScopeByKey,
  orderRequiresBarcode,
  orderBarcodeResolutionMissing,
  orderServicesMissingFieldKeys,
  changedFulfillmentTargets,
  BARCODE_RESOLUTION_FIELD_KEY,
  DELIVERY_MODE_FIELD_KEY,
  parseFulfillmentTargetFieldKey,
  PROOF_QUANTITY_FIELD_KEY,
  PROOF_SHIP_TO_FIELD_PREFIX,
  proofDeliveryMissingFieldKeys,
  resolveQuoteProofType,
  shipToFieldLabel,
  shipToMissingFieldKeys,
  splitBaseQuantity,
  type LineConfigIssue,
  type LineFulfillmentTarget,
  type MediaContentQuoteStateResponse,
  type OrderServicesSnapshot,
  type ShipTo,
} from 'shared';

import { extractApiError } from '../api/client.js';
import * as quotesApi from '../api/quotes.js';
import * as schemaApi from '../api/schema.js';
import * as submissionApi from '../api/submission.js';
import { ANALYTICS_EVENTS, trackEvent } from '../lib/analytics/index.js';
import * as uploadsApi from '../api/uploads.js';
import { useIsImpersonating } from '../auth/AuthContext.js';
import { usePricingLocked } from '../config/ConfigContext.js';
import { ArtworkUploader } from '../components/ArtworkUploader.js';
import type { ArtworkPendingToggleFor } from '../components/UploadSlot.js';
import { deriveProjectName } from '../components/deriveProjectName.js';
import { lineServesDoor } from '../components/entryDoorParts.js';
import { AssemblyPanel } from '../components/AssemblyControl.js';
import { RowRecap } from '../components/CollapsibleRows.js';
import { ChevronDownIcon } from '../components/ControlIcons.js';
import { newKitContentRowId, type KitContentLineOption } from '../components/KitContentsEditor.js';
import { NeedByPicker } from '../components/NeedByPicker.js';
import { PricingBlock } from '../components/PricingBlock.js';
import { QuoteApprovalPanel } from '../components/QuoteApprovalPanel.js';
import { SavedPriceSummary } from '../components/SavedPriceSummary.js';
import { OrderServicesForm } from '../components/OrderServicesForm.js';
import type { FulfillmentItem } from '../components/FulfillmentFields.js';
import {
  withKitPickReconciled,
  withLineFulfillmentTarget,
} from '../components/fulfillmentTargets.js';
import {
  SchemaForm,
  deriveUploadSlots,
  inlineUploadRoles,
  MEDIA_CONTENT_FILE_FIELD_KEY,
  shownFieldLabel,
  type FieldValues,
} from '../components/SchemaForm.js';
import {
  ProjectSummaryStrip,
  type SummaryStripItem,
} from '../components/ProjectSummaryStrip.js';
import { SpecSummary } from '../components/SpecSummary.js';
import { TechnologyHandoff } from '../components/TechnologyHandoff.js';
import { useLineAssets } from '../components/useLineAssets.js';
import {
  mediaContentLineState,
  mediaContentReadiness,
  useMediaContentQuoteState,
} from '../components/useMediaContentState.js';
import { useArtworkPreview } from '../components/useArtworkPreview.js';
import { useAssemblyPhotos } from '../components/useAssemblyPhotos.js';
import { familyLabel, type FamilyCode } from '../components/FamilyGlyph.js';
import {
  clearQuoteDraft,
  draftIsCurrent,
  readQuoteDraft,
  writeQuoteDraft,
} from '../components/quoteDraft.js';
import {
  customerFacingFailure,
  DEFAULT_SALES_REP,
  salesRepFor,
  SUPPORT_PHONE,
} from '../pricingCopy.js';
import { formatCurrency } from '../i18n/formatters.js';
import { quoteStatusLabel } from '../i18n/quoteStatus.js';

type LineDraft = {
  // local id only — Quote line id from the backend, or a synthetic
  // string for newly-added lines about to be PATCHed.
  local_id: string;
  title: string;
  quantity: number;
  config: FieldValues;
  // unit_price/line_price flow in from the loaded quote for display.
  unit_price: string | null;
  line_price: string | null;
};

// Synthetic local ids assigned to newly-added (unsaved) lines by
// addLine() use the "new-<timestamp>" shape; persisted lines carry the
// real DB id. Artwork attaches to a QuoteLine by id, so the uploader
// only renders for persisted lines — new lines show a "save first"
// placeholder instead.
function isUnsavedLine(local_id: string): boolean {
  return local_id.startsWith('new-');
}

// Today as a local-time `YYYY-MM-DD` (matches the shape an <input type="date">
// emits and Quote.requested_finish_date serializes to). Local, not UTC, so the
// default Need-By and the expedited check line up with the customer's calendar.
function todayLocalIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Does this line render an upload slot that carries an "art to follow"
// checkbox? Only the slots it can mark do (linePendableArtworkRoles) — so a
// family with an empty role set (assembly, "something else") shows it nowhere,
// however expanded and saved the item is.
function lineHostsArtworkPendingToggle(config: FieldValues): boolean {
  return linePendableArtworkRoles(config).length > 0;
}

// An older draft's quote-level "art to follow" tick, seeded onto one line: the
// server line's marks when it holds some, else every slot the line can mark.
// A slot whose file is already on the line drops its mark when the line's
// assets load (LineEditor clears it).
function withLegacyArtworkPending(
  config: FieldValues,
  serverConfig: FieldValues | undefined
): FieldValues {
  if (serverConfig && ARTWORK_PENDING_ROLES_CONFIG_KEY in serverConfig) {
    return { ...config, [ARTWORK_PENDING_ROLES_CONFIG_KEY]: serverConfig[ARTWORK_PENDING_ROLES_CONFIG_KEY] };
  }
  return linePendableArtworkRoles(config).reduce(
    (next, role) => withArtworkPendingRole(next, role, true),
    config
  );
}

// The line config with every "art to follow" mark cleared.
function withoutArtworkPending(config: FieldValues): FieldValues {
  const { [ARTWORK_PENDING_ROLES_CONFIG_KEY]: _cleared, ...rest } = config;
  return rest;
}

function lineProductType(line: LineDraft): string | undefined {
  const v = line.config['product_type'];
  return typeof v === 'string' ? v : undefined;
}

// A hard-bound (case-bound) book line. As of the soft/hard family split this is
// simply the `hard_bound_book` product type — no more cover_binding_style toggle.
// A dust jacket is only offered as a project once the quote has one of these on
// another line (we never print a dust jacket on its own).
// An Assembly item: the thing bought IS the kitting work, and the one assembly
// flow (ADR-020), so the item mounts the Assembly panel bound to its own
// snapshot. The snapshot lives in this line's `config` under `assembly`, which
// persists and transmits with the rest of the line config (R5) and needs no
// column.
// A line's assembly snapshot off its config, which reaches here as untyped JSON:
// `config` is FieldValues, nothing parses it on the load path, and an older draft
// or a hand-edited localStorage blob can hold anything. Shape is checked rather
// than asserted, on both readers — the panel would throw at RENDER on a
// `kit_contents` that is not an array, and healAssembly runs AFTER the PATCH
// resolved, so a throw there would report a save that actually succeeded as
// failed.
function configAssembly(config: unknown): AssemblySnapshot | undefined {
  if (typeof config !== 'object' || config === null) return undefined;
  const raw = (config as Record<string, unknown>)[ASSEMBLY_ITEM_CONFIG_KEY];
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  // The two lists are read as arrays the moment the panel renders (`?? []` only
  // substitutes for null), so a truthy non-array is the shape that crashes. It is
  // dropped rather than passed on: the answer it held is unreadable either way.
  const out: AssemblySnapshot = { ...(raw as AssemblySnapshot) };
  if (!Array.isArray(out.kit_contents)) delete out.kit_contents;
  if (!Array.isArray(out.build_steps)) delete out.build_steps;
  return out;
}

function isAssemblyLine(line: LineDraft): boolean {
  return line.config.product_type === ASSEMBLY_ITEM_PRODUCT_TYPE;
}

// A "Something else" item: the product picker's escape hatch. It renders the
// hand-off panel instead of a spec, and asks for no artwork.
function isSomethingElseLine(line: LineDraft): boolean {
  return line.config.product_type === 'something_else';
}

function isHardcoverBookLine(line: LineDraft): boolean {
  return lineProductType(line) === 'hard_bound_book';
}

// Recognize the portal's pricing-failure envelope `{ ok: false, failure: {...} }`
// inside an axios error so callers can route it through PricingBlock with
// the structured field_keys / fallback_action intact, instead of dropping
// to a generic "try again" banner.
function extractPricingFailure(err: unknown): PricingFailureDto | null {
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof (err as { response: unknown }).response === 'object'
  ) {
    const data = (err as { response: { data?: unknown } }).response?.data;
    if (
      typeof data === 'object' &&
      data !== null &&
      'ok' in data &&
      (data as { ok: unknown }).ok === false &&
      'failure' in data
    ) {
      const failure = (data as { failure: unknown }).failure;
      if (
        typeof failure === 'object' &&
        failure !== null &&
        'code' in failure &&
        typeof (failure as { code: unknown }).code === 'string'
      ) {
        return failure as PricingFailureDto;
      }
    }
  }
  return null;
}

// Pre-flight: collect required fields the customer hasn't filled in.
// Mirrors the same conditional_show evaluation the SchemaForm renderer
// uses, so a field hidden by conditional rules is never counted as
// missing. Saves a round trip on the obvious case and lets us anchor
// the error UX (inline + near-button banner + scroll-to-field) entirely
// on the client.
//
// Per UX research (Nielsen Norman, "Inline Validation"):
//   - One inline error per field, near the field, in red.
//   - One summary near the submit button listing all errors.
//   - Click-to-jump from the summary entry to the field.
// We deliver both.
// The blank printed-sample address the proof block opens with. A module-level
// constant so its identity is stable across renders — a fresh array literal in
// the render would remount the sample list on every keystroke elsewhere.
const SEEDED_PROOF_SHIP_TOS: ShipTo[] = [{ delivery_method: 'ship' }];

type PreflightMiss = {
  field_key: string;
  field_label: string;
  // local_id of the line where the field is missing — drives the
  // click-to-scroll target.
  line_local_id: string;
  line_index: number;
  // Why the field is flagged: 'missing' (required + empty), 'invalid_value' (a
  // value not in the schema's accepted option set), or 'missing_upload' (a
  // required artwork file isn't uploaded — a file_reference field, judged off
  // Asset state rather than `config`). Drives the inline copy. Optional because
  // quote-level (ship_to / order_services) misses carry no schema reason — they
  // default to 'missing'.
  reason?: LineConfigIssue['reason'] | 'missing_upload';
  // Quote-level surfaces whose misses aren't tied to a project, so the summary
  // skips the "(project N)" suffix: 'ship_to' (step 12 destination) and
  // 'order_services' (step 14 fulfillment details). 'unanchored' is
  // the third case: a field key a blocking outcome named that this page renders
  // no control for (an ERP key the served schema doesn't carry). It still gets
  // named in the summary — as plain text, since there is nothing to scroll to —
  // because a block the customer can't see the reason for is the defect (FB-0051).
  scope?: 'ship_to' | 'order_services' | 'unanchored';
  // The server's own sentence for this field, printed in the summary when the
  // page has no control to carry it inline (an 'unanchored' miss).
  detail?: string;
};

// DOM id / error-map key for a per-line schema field, namespaced by the line's
// local_id. A quote can hold several projects rendered from the SAME schema, so
// a bare field key (e.g. `cover_finished_size`) would collide across projects:
// document.getElementById would only ever find project 1's copy (so the summary
// link couldn't scroll to project 2's field), and a bare-keyed error map would
// light the inline error on EVERY project's copy of the field, not just the one
// actually missing it. Namespacing both the input id and the error key fixes
// both. Quote-level surfaces (ship_to / order_services) are singletons and keep
// their bare keys (their DOM ids already equal the bare field key).
function lineFieldKey(lineLocalId: string, fieldKey: string): string {
  return `${lineLocalId}::${fieldKey}`;
}

// The DOM id to scroll to for a given miss: quote-level misses target the bare
// field key (ShipToForm / OrderServicesForm render those ids directly); per-line
// misses target the line-namespaced id SchemaForm renders.
function missDomId(miss: PreflightMiss): string {
  return miss.scope ? miss.field_key : lineFieldKey(miss.line_local_id, miss.field_key);
}

// Collect every issue across all line drafts using the ONE shared pair of
// validators — the SAME routines the backend price gate runs:
//   - lineConfigIssues  → required/accepted-set CONFIG fields, and
//   - lineMissingUploadFieldKeys → required ARTWORK uploads (validateRoleSet),
//     the same rule the submit gate enforces, so a job can't be priceable yet
//     rejected at submit for a missing cover/manuscript.
// This is what makes "Save" and "See my price" judge a quote identically (and
// makes the result the same whether pricing is mocked or real): both call this.
// A field hidden by conditional_show is never counted (the validator shares
// evalExpr with the renderer). See docs/ADR-011.
//
// `presentRoles` is the quote-wide set of component_roles that already have an
// upload (pooled across lines to match the submit gate). Pass null while the
// asset list is still loading — upload misses are then skipped and we defer to
// the authoritative backend gate, exactly as the variable-data pre-block does.
//
// Each line's "quote now, art to follow" marks (linePendingArtworkRoles) waive
// THOSE slots of the upload check and no others, matching the backend price +
// submit gates — a ticked cover leaves the manuscript required. Config/spec
// fields and the variable-data spreadsheet requirement are unaffected.
//
// `mediaState` is the quote's USB / disc content state (ADR-021 §7), from which
// each media line's readiness is judged by the same rule the backend gates use.
// Null while it loads: the content check is then left to the backend gate.
function collectConfigMisses(
  schema: FormSchemaDto,
  drafts: LineDraft[],
  presentRoles: ReadonlySet<ComponentRole> | null,
  mediaState: MediaContentQuoteStateResponse | null,
): PreflightMiss[] {
  // Field display_order lookup so a line's misses read in form order — the
  // manuscript upload (order 90) lands above its Page Count, so "jump to the
  // first problem" hits the earliest field on the form, not the first config one.
  const orderByKey = new Map<string, number>();
  for (const f of schema.fields) orderByKey.set(f.key, f.display_order);

  const misses: PreflightMiss[] = [];
  drafts.forEach((d, idx) => {
    const lineMisses: PreflightMiss[] = [];
    for (const issue of lineConfigIssues(schema, d.config, { lineTitle: d.title })) {
      lineMisses.push({
        field_key: issue.field_key,
        field_label: issue.field_label,
        line_local_id: d.local_id,
        line_index: idx,
        reason: issue.reason,
      });
    }
    if (presentRoles) {
      const pt = typeof d.config.product_type === 'string' ? d.config.product_type : '';
      const upMisses = lineMissingUploadFieldKeys(pt, presentRoles, {
        selfCover: d.config.self_cover === true,
        combinedArtwork: d.config.combined_artwork === true,
        packagingArt: mediaPackagingCarriesArt(d.config),
        customPackagingArt: customPackagingCarriesArt(d.config),
        needsVariableDataList: lineNeedsVariableDataList(d.config),
        mediaContent: mediaState
          ? mediaContentReadiness(d.config, mediaContentLineState(mediaState, d.local_id))
          : undefined,
        artworkPendingRoles: linePendingArtworkRoles(d.config),
      });
      for (const fieldKey of upMisses) {
        const fld = schema.fields.find((f) => f.key === fieldKey);
        lineMisses.push({
          field_key: fieldKey,
          field_label: fld?.label ?? fieldKey,
          line_local_id: d.local_id,
          line_index: idx,
          reason: 'missing_upload',
        });
      }
    }
    lineMisses.sort(
      (a, b) =>
        (orderByKey.get(a.field_key) ?? Number.POSITIVE_INFINITY) -
        (orderByKey.get(b.field_key) ?? Number.POSITIVE_INFINITY),
    );
    misses.push(...lineMisses);
  });
  return misses;
}

// Inline message for a flagged field, by reason. Centralized so the pre-flight
// (price) and the save report show identical copy.
function missInlineMessage(miss: PreflightMiss): string {
  if (miss.reason === 'invalid_value') {
    return i18n.t('quote:edit.fieldError.invalidOption');
  }
  if (miss.reason === 'missing_upload' && miss.field_key === MEDIA_CONTENT_LINK_CONFIG_KEY) {
    return i18n.t('quote:mediaContent.gate.linkNotReady');
  }
  if (miss.reason === 'missing_upload' && miss.field_key === MEDIA_CONTENT_FILE_FIELD_KEY) {
    return i18n.t('quote:mediaContent.gate.uploadNotReady');
  }
  if (miss.reason === 'missing_upload') {
    return i18n.t('quote:edit.fieldError.uploadNeeded');
  }
  return i18n.t('quote:edit.fieldError.fillNeeded');
}

// The Nexus quote number this order shows, in the header and the approval panel
// alike: the one stored with its binding. Until a link has stored it, an approved
// quote's number counts only when Nexus reports it for the opportunity this order
// is bound to — the status read stores it under that same rule, so the two can
// never show different numbers.
function boundNexusQuoteNumber(
  quote: Pick<QuoteDto, 'nexus_opportunity_id' | 'nexus_quote_number'>,
  status: QuoteStatusDto | null,
): string | null {
  if (quote.nexus_opportunity_id === null) return null;
  if (quote.nexus_quote_number) return quote.nexus_quote_number;
  const approved = status?.approved_quote;
  return approved && approved.opportunity_id === quote.nexus_opportunity_id
    ? approved.quote_number
    : null;
}

// What this project is actually set to print. Quantity is dual-homed — a
// structural QuoteLine column AND a schema field, mirrored on load — and the
// schema-form copy is the live edit surface, so it wins. One reader, used by the
// save mapping, the collapsed recap and the pricing split alike; hand-rolled
// copies of this coercion is how two of them end up disagreeing about what the
// customer typed.
function draftQuantity(d: LineDraft): number {
  return typeof d.config.quantity === 'number' && d.config.quantity > 0
    ? (d.config.quantity as number)
    : d.quantity;
}

// JSON with every object's keys sorted, so two copies of one answer compare
// equal however their keys were inserted: a config read back from the server's
// JSONB arrives key-reordered, while one that went through the local draft keeps
// the editor's insertion order.
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v !== null && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([a], [b]) =>
            a < b ? -1 : a > b ? 1 : 0
          )
        )
      : v
  );
}

type PersistableSelections = {
  title: string;
  finishDate: string;
  expediteRequested: boolean;
  shipTos: ShipTo[];
  proofQuantity: number | null;
  proofShipTos: ShipTo[];
  orderServices: OrderServicesSnapshot;
  lines: LineDraft[];
};

// Everything a save writes, as one comparable string: the autosave's dirty test.
// Lines are compared by position as (title, quantity, config) and never by
// local_id, which a save remaps from a synthetic "new-…" id to the real one
// without the customer changing anything. Prices are not part of it: they are
// the server's, never something the editor sends.
function persistableSnapshot(s: PersistableSelections): string {
  return stableStringify({
    project_title: s.title,
    requested_finish_date: s.finishDate,
    expedite_requested: s.expediteRequested,
    ship_tos: s.shipTos,
    proof_quantity: s.proofQuantity,
    proof_ship_tos: s.proofShipTos,
    order_services: s.orderServices,
    lines: s.lines.map((d) => ({ title: d.title, quantity: draftQuantity(d), config: d.config })),
  });
}

// The server version a local draft is built on, for the draft's staleness test
// (quoteDraft.ts draftIsCurrent). The selections plus each line's id: two copies
// with identical content but recreated lines are different versions, and a draft
// holding the old ids would send them on its next save.
function draftBaseSnapshot(s: PersistableSelections): string {
  return stableStringify({
    selections: persistableSnapshot(s),
    line_ids: s.lines.map((d) => d.local_id),
  });
}

type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// The editor's save options; QuoteEdit's handleSave documents each one.
type SaveOptions = {
  silent?: boolean;
  lines?: LineDraft[];
  auto?: boolean;
};

function quoteToDrafts(quote: QuoteDto): LineDraft[] {
  return quote.lines.map((l) => {
    const baseConfig = (l.config as FieldValues) ?? {};
    // Quantity is BOTH a structural QuoteLine column and a schema
    // field (so the configurator can offer family-aware tips +
    // validation). Mirror them on read so the schema renderer has a
    // value. The save handler does the inverse: extract config.quantity
    // back to QuoteLine.quantity at the API boundary.
    let config: FieldValues =
      baseConfig.quantity == null ? { ...baseConfig, quantity: l.quantity } : baseConfig;
    // proof_type became a multi-select (string[]); legacy lines may have stored
    // it as a single string. Normalize to an array so the multi_enum renderer
    // shows a saved 'digital' as checked — and a legacy 'none' as cleared, which
    // the now-required field then re-prompts for. (readLineProofType already
    // tolerates both shapes for pricing/gating; this is for the editor display.)
    if (typeof config.proof_type === 'string') {
      config = { ...config, proof_type: [config.proof_type] };
    }
    // Finishing-section split (UI cleanup): the finishing ops were one
    // config.finishing array; they're now three controls — the in-house
    // checklist (config.finishing), the standalone Shrinkwrapping toggle
    // (config.shrinkwrap), and the Specialty Finishing section
    // (config.specialty_finishing). Split a legacy array on load so an existing
    // quote's shrinkwrap + specialty picks aren't stranded: scrub would
    // otherwise drop them from config.finishing (which now offers in-house ops
    // only) on the first edit. Pricing already tolerates the legacy shape
    // (readFinishingOps reads config.finishing too), so this is display-only —
    // mirrors the proof_type migration above. Idempotent: only moves values
    // still sitting in config.finishing, and never clobbers a destination key
    // already populated (an already-migrated or newly-created quote no-ops).
    if (Array.isArray(config.finishing)) {
      const arr = config.finishing.filter((v): v is string => typeof v === 'string');
      const hasShrink = arr.includes(SHRINKWRAP_FAMILY_KEY);
      const specialtyPicked = arr.filter((v) => SPECIALTY_FINISHING_KEYS.includes(v));
      if (hasShrink || specialtyPicked.length > 0) {
        const inHouse = arr.filter(
          (v) => v !== SHRINKWRAP_FAMILY_KEY && !SPECIALTY_FINISHING_KEYS.includes(v),
        );
        // The legacy values always leave config.finishing — that control no
        // longer offers them, so anything left behind is unreachable by the UI
        // yet still priced (readFinishingOps unions all four sources), which
        // would let a stale array entry outvote an explicit "off" on the new
        // control. The specialty picks are therefore UNIONED into whatever the
        // destination already holds rather than skipped when it is populated:
        // skipping dropped the legacy pick from both keys on a line carrying
        // the old array and a populated new key at once. The shrinkwrap boolean
        // takes the opposite resolution because it has no union — an explicit
        // false is the newer answer and wins over the legacy entry.
        const existingSpecialty = config[SPECIALTY_FINISHING_CONFIG_KEY];
        const mergedSpecialty = Array.isArray(existingSpecialty)
          ? [
              ...existingSpecialty,
              ...specialtyPicked.filter((v) => !existingSpecialty.includes(v)),
            ]
          : specialtyPicked;
        config = {
          ...config,
          [FINISHING_CONFIG_KEY]: inHouse,
          ...(hasShrink && config[SHRINKWRAP_CONFIG_KEY] === undefined
            ? { [SHRINKWRAP_CONFIG_KEY]: true }
            : {}),
          ...(specialtyPicked.length > 0
            ? { [SPECIALTY_FINISHING_CONFIG_KEY]: mergedSpecialty }
            : {}),
        };
      }
    }
    return {
      local_id: l.id,
      title: l.title,
      quantity: l.quantity,
      config,
      unit_price: l.unit_price,
      line_price: l.line_price,
    };
  });
}

// The server's copy of everything the editor persists, as the editor hydrates
// it. The load effect hydrates from it, and a save re-bases the local draft on
// the copy it returned, so the two compare equal whenever the server's
// selections have not moved.
//
// Need-By defaults to today + the standard turnaround window when the quote has
// none, so a new quote opens on the "about a week" default (the customer can pull
// it in — which flips it to an expedited order). The window is family-aware: a
// hard-bound project stretches the default out to its longer turnaround
// (turnaroundDaysForProductTypes takes the longest across lines). Only while the
// quote can still be edited. Past that the default has nothing left to offer and
// `todayLocalIso()` is a MOVING input: an order that stored no date would read
// back a different in-hands date every day it is opened, presented as the date
// the customer asked for (R26 — the same shape as the `expedite_requested`
// defect). Stored-or-nothing, and NeedByPicker's locked arm reads nothing back as
// the em-dash.
//
// A quote with no services snapshot opens with NO mode lit, new or old: its
// rows' delivery_method was only ever a stamp, so reading Pickup (or Shipping)
// off it would put an answer in the customer's mouth, and the submit gate asks
// them instead. The address list arrives already reconciled against the services
// snapshot by the serializer; empty means nothing captured yet, which the editor
// seeds with one blank shipping address. The printed samples' addresses
// round-trip as their own list and are not seeded here: the seed belongs with
// the condition that reveals the block (a physical proof), not with the load.
function serverSelections(q: QuoteDto, editable: boolean): PersistableSelections {
  const lines = quoteToDrafts(q);
  const turnaroundDays = turnaroundDaysForProductTypes(lines.map((d) => lineProductType(d)));
  return {
    title: q.project_title ?? '',
    finishDate:
      q.requested_finish_date ??
      (editable ? defaultNeedByDate(todayLocalIso(), turnaroundDays) : ''),
    expediteRequested: q.expedite_requested,
    shipTos: q.ship_tos.length > 0 ? q.ship_tos : [{ delivery_method: 'ship' }],
    proofQuantity: q.proof_quantity,
    proofShipTos: q.proof_ship_tos,
    orderServices: q.order_services ?? { services_requested: [] },
    lines,
  };
}

export function QuoteEdit(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  // The door a Nexus arrival on this order came through (`?entry_division_key=`),
  // or null. It selects that door's item on landing and does nothing else.
  const [search] = useSearchParams();
  const requestedDoor = search.get('entry_division_key');
  const arrivalDoor = isEntryDivision(requestedDoor) ? requestedDoor : null;
  const { t } = useTranslation(['quote', 'common']);
  // Payment is blocked during impersonation (ADR-008 §3) — drives whether
  // the approval panel offers a pay button.
  const impersonating = useIsImpersonating();
  const [drafts, setDrafts] = useState<LineDraft[] | null>(null);
  const [title, setTitle] = useState<string>('');
  const [finishDate, setFinishDate] = useState<string>('');
  // The customer's explicit rush choice (Quote.expedite_requested) — the
  // Schedule card's "I need it sooner" lane. Round-trips from the server flag;
  // sent on Save so Nexus prices the +10% from the pick, not from a date calc.
  const [expediteRequested, setExpediteRequested] = useState<boolean>(false);
  // "Quote now, art to follow" on any slot of any line — read off the live line
  // configs, where each slot's mark is stored, by the same rule the server uses
  // to re-derive Quote.artwork_pending. Drives the disclaimer near the price.
  const artworkPending = quoteArtworkPending((drafts ?? []).map((d) => d.config));
  // Where the order ships (step 12) — every address, in the customer's order,
  // each carrying the pieces going there. Round-trips from quote.ship_tos on
  // load; persisted via a snapshot-only PATCH (no lines) so saving it never
  // clears the price snapshot. delivery_method is a stamp derived from the
  // delivery mode the order-services cards store (reconcileDeliveryIntent), held
  // on every entry so nothing downstream has to pick one to believe.
  const [shipTos, setShipTos] = useState<ShipTo[]>([{ delivery_method: 'ship' }]);
  // The printed proof sample (FB-0036) — how many, and where each is mailed.
  // Quote-level, like the addresses above and persisted the same snapshot-only
  // way, and only asked once the quote resolves to a PHYSICAL proof. The count
  // starts null (unanswered) rather than at 1: a pre-filled number the customer
  // never read is not an answer, and the submit gate reports it as a miss on a
  // control that is on screen.
  const [proofQuantity, setProofQuantity] = useState<number | null>(null);
  const [proofShipTos, setProofShipTos] = useState<ShipTo[]>([]);
  // Quote-level order services (step 14) — "what do you need us to do with it?"
  // — holding the ONE delivery mode the customer picked (shipping / fulfillment /
  // pickup), or none while the customer has not answered. No quote opens on a
  // pre-lit card: a mode stored without a click would cross to Nexus as an
  // answer nobody gave. Round-trips from quote.order_services; persisted the
  // same snapshot-only way as ship_to.
  const [orderServices, setOrderServices] = useState<OrderServicesSnapshot>({
    services_requested: [],
  });
  const [saving, setSaving] = useState(false);
  // Transient "Saved" confirmation after an explicit Save click. Without
  // it a fast, successful save shows no feedback at all — the button just
  // flickers "Saving…" and reverts, reading as "nothing happened".
  const [savedNotice, setSavedNotice] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The quiet autosave's own outcome, read only by the status line beside Save.
  // It never drives the "Saved" banner, the field errors or the summary: those
  // answer a button the customer clicked, and an autosave is not one.
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  // The selections the server last confirmed holding (persistableSnapshot) — the
  // baseline the autosave's dirty test compares against. Set by the load effect
  // and by every successful save, to what that save actually sent, so a keystroke
  // landed during the round-trip still reads as unsaved afterwards.
  const lastSavedSnapshotRef = useRef<string | null>(null);
  // The selections the last failed autosave tried to send. The same answer is not
  // re-sent on every click or focus change while it keeps failing; the next edit
  // makes a different snapshot, and that is the retry.
  const failedSnapshotRef = useRef<string | null>(null);
  // Which quote the load effect last hydrated, so its overlay branch can tell a
  // first open (restored unsaved edits are dirty against the server) from the
  // refetch after its own save (the baseline that save set stands).
  const hydratedQuoteIdRef = useRef<string | null>(null);
  // Saves between their PATCH and their draft re-stamp, by quote id, and whether
  // the load effect held back a hydrate for one of them. The nudge re-runs that
  // effect once the last such save settles: the refetch the save makes next can
  // return the very copy already held, which would not re-run it on its own.
  const savesInFlightRef = useRef(new Map<string, number>());
  const deferredHydrateRef = useRef(false);
  const [hydrateNudge, setHydrateNudge] = useState(0);
  // Bumped only by a button the customer clicked (Save, See my price, Place my
  // order). SchemaForm shows a field's validation-rule errors as they stood at
  // the last bump, and lets each one drop as it is fixed, so typing never paints
  // a new red message and an autosave never reveals one.
  const [validationRevealSeq, setValidationRevealSeq] = useState(0);
  const [pricingState, setPricingState] = useState<
    | { kind: 'idle' }
    | { kind: 'pricing' }
    | { kind: 'priced'; response: PricingResponseDto }
  >({ kind: 'idle' });
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Set when the customer clicked "Submit for quote" (the CANNOT_PRICE
  // follow-up action — see below) and the resulting attempt confirmed
  // CANNOT_PRICE again. Swaps the generic contact-sales PricingBlock banner
  // for a warm "we got it" confirmation so the second click reads as having
  // done something, instead of repeating the same banner verbatim.
  const [customQuoteConfirmed, setCustomQuoteConfirmed] = useState(false);
  const [submitState, setSubmitState] = useState<
    | { kind: 'idle' }
    | { kind: 'submitting' }
    | { kind: 'repriced'; previous: number; next: number; message: string }
    | { kind: 'pending_approval'; message: string }
  >({ kind: 'idle' });

  // Identity-prompt modal. The first submit attempt by a contact user
  // without an existing Nexus relationship returns `identity_required`;
  // we open the modal, capture (name + optional company), then call
  // submitQuote again with the new identity body. The hosted text and
  // form fields are kept inside this surface so we never lose the
  // submit context if the user re-tries.
  const [identityPrompt, setIdentityPrompt] = useState<
    | { kind: 'closed' }
    | { kind: 'open'; message: string; name: string; company_name: string }
  >({ kind: 'closed' });

  // Unified action-feedback for the near-button area. Carries a
  // heading + a field-keyed list (preflight, ERP pricing rejection,
  // save reject, submit reject). Inline red text on individual fields
  // is driven off `fieldErrors` and stays in sync with `misses`.
  //
  // `source` lets the live-reconcile effect only re-run the client-side
  // pre-flight when the list came from that check. ERP-rejected field
  // keys are cleared on the user's next click, not on edit (re-checking
  // requires a round trip).
  // Which items are collapsed, by local_id. Held here rather than inside
  // LineEditor because revealMiss has to be able to open the item a flagged
  // field lives in before scrolling to it, and removeLine has to prune the item
  // it deletes. Seeded on load with the finished items (see foldedOnLoadRef).
  const [collapsedLines, setCollapsedLines] = useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  const [fieldSummary, setFieldSummary] = useState<{
    source: 'preflight' | 'pricing' | 'save' | 'submit';
    heading: string;
    trailing?: string;
    misses: PreflightMiss[];
  } | null>(null);

  // Scroll the price block into view whenever pricing lands (success
  // or failure). Without this, on a long form the response renders far
  // below the fold and the customer reads it as "nothing happened".
  const pricingBlockRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (pricingState.kind === 'priced' && pricingBlockRef.current) {
      pricingBlockRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pricingState]);

  const queryClient = useQueryClient();
  const quoteQuery = useQuery({
    queryKey: ['quote', id],
    queryFn: () => quotesApi.getQuote(id!),
    enabled: Boolean(id),
  });

  const schemaQuery = useQuery({
    queryKey: ['schema'],
    queryFn: schemaApi.getActiveSchema,
  });

  // Quote-wide uploaded assets — used only to know which component_roles are
  // present so the client can PRE-BLOCK submit when variable data is on but the
  // 'variable_data_list' spreadsheet hasn't been uploaded (step 8). The backend
  // gate is authoritative; this just spares a round trip and reuses the inline +
  // near-button + reveal UX. Refetched via each uploader's onAssetsChanged so
  // the pre-block deflates the moment the file lands. NOT a price cache.
  const assetsQuery = useQuery({
    queryKey: ['quote-assets', id],
    queryFn: () => uploadsApi.listAssets(id!),
    enabled: Boolean(id),
  });

  // USB / disc content state per line (ADR-021 §7) — the content half of the
  // pricing pre-flight. Shares its query key with the uploader and link-check
  // controls, which update and invalidate it after a finalize, check or cancel.
  const mediaContentQuery = useMediaContentQuoteState(id);

  // Quote-wide set of component_roles that already have a (non-deleted) upload,
  // pooled across all lines to match the submit gate (getQuoteRoleStatus). Drives
  // the required-artwork half of the pricing pre-flight (collectConfigMisses). Null
  // while the asset list is still in flight — upload misses are then skipped and
  // the authoritative backend gate covers it, exactly like the variable-data
  // pre-block. NOT a price cache (roles only; CLAUDE.md §Caching).
  const presentRoles = useMemo<ReadonlySet<ComponentRole> | null>(() => {
    if (assetsQuery.data === undefined) return null;
    const roles = new Set<ComponentRole>();
    for (const a of assetsQuery.data) roles.add(a.component_role);
    return roles;
  }, [assetsQuery.data]);

  // Nexus's authoritative (control + approval_state) verdict for this quote.
  // Drives the spec-editor lock + the pay surface. Real-time, no caching:
  // the portal reads both facts and computes neither (CLAUDE.md). Polls
  // while a human review is pending so the surface flips to payable the
  // moment Nexus approves. A 503 (status unavailable) degrades to today's
  // behavior — the /api/erp/pricing SALES_CONTROLLED guard is the backstop.
  const statusQuery = useQuery({
    queryKey: ['quote-status', id],
    queryFn: () => quotesApi.getQuoteStatus(id!),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d || !d.ok) return false;
      if (d.status.approval_state === 'pending') return 4000;
      // An open change order closes from Nexus's Signature stage, not from here,
      // so the page keeps asking until it does and relocks without a reload.
      return d.status.change_order ? 30000 : false;
    },
  });

  // Nexus verdict (null while loading, on a 503, or when status is
  // unavailable — all of which fall back to today's self-service behavior).
  const quoteStatus = statusQuery.data?.ok ? statusQuery.data.status : null;
  // Nexus owns this quote (a rep took it over), so pricing is Nexus's to
  // author — the portal never computes or offers "See my price" while sales
  // controls it. Pricing stays withheld for the whole sales-controlled span.
  const salesControlled = quoteStatus?.control === 'sales';
  // Nexus has re-opened this placed order (shared ChangeOrderDto), and the person
  // at the keyboard is an Argo rep impersonating the partner — the only one a
  // change order unlocks the order for. The partner on their own session keeps
  // today's read-only view. The backend re-asks Nexus on every write, so this
  // only decides what the page offers.
  const changeOrderOpen =
    impersonating &&
    Boolean(quoteStatus?.change_order) &&
    isChangeOrderEditableStatus(quoteQuery.data?.status ?? '');
  // A rep-authored order Nexus still holds open (shared isSalesQuoteOpenForRep):
  // the rep keeps editing it as they did while it was a draft, approved quote or
  // not, because the Opportunity behind it is still editable in Nexus.
  const repUnlocked =
    changeOrderOpen ||
    (impersonating &&
      quoteStatus !== null &&
      isSalesQuoteOpenForRep(quoteQuery.data?.status ?? '', quoteStatus));
  // Once the rep has AUTHORED an approved quote, the line set is Nexus's: the
  // partner views it (and pays) in the panel above and the spec editor goes
  // read-only. Until then — the "being prepared by your Argo rep" state — the
  // partner can still refine their spec and save; we simply don't show or
  // compute a price. The rep is never locked out while Nexus has the order open.
  const salesQuoteLocked =
    salesControlled && Boolean(quoteStatus?.approved_quote) && !repUnlocked;
  // Whether this order still takes edits. Declared HERE, above every effect that
  // has to respect it, rather than beside the render it gates: an effect that
  // writes to an order past editing produces a phantom line in local state and a
  // PATCH the backend refuses.
  const isEditable =
    (quoteQuery.data?.status === 'draft' ||
      quoteQuery.data?.status === 'priced' ||
      repUnlocked) &&
    !salesQuoteLocked;

  // Baseline the local-draft buffer reconciles against: the loaded quote's
  // updated_at, whether it's editable, and the server's selections as one
  // comparable string. Written by the load effect and by every save, read by the
  // write effect — a ref (not state) so the write effect never fires in the same
  // pass the load effect resets state (which would stamp the new baseline onto
  // the PREVIOUS render's selections, and could clobber another tab's save). It
  // re-fires one render later, once the selection state matches the baseline.
  const draftBaselineRef = useRef<{ updated_at: string; editable: boolean; snapshot: string } | null>(
    null
  );
  // Which quote `drafts` was hydrated from. State, set beside setDrafts, so it
  // commits in the same render as the lines it describes: the route keeps this
  // page mounted across a change of `:id`, and the previous order's lines sit
  // in `drafts` until the next order's load lands.
  const [draftsQuoteId, setDraftsQuoteId] = useState<string | null>(null);

  useEffect(() => {
    const q = quoteQuery.data;
    if (!q) return;

    // A dependency of this effect, so the load re-runs when the rep's unlock opens
    // or closes: it re-baselines the local-draft buffer, and on the close drops
    // any unsaved edits for the server's copy as the order relocks.
    const editable = q.status === 'draft' || q.status === 'priced' || repUnlocked;
    // A save of this quote has written but not yet re-stamped the draft, so the
    // draft cannot be judged against this copy: it would read as stale and the
    // server copy would replace whatever the customer picked after the save's
    // body was built. Held until that save settles (see savesInFlightRef).
    if (
      editable &&
      hydratedQuoteIdRef.current === q.id &&
      (savesInFlightRef.current.get(q.id) ?? 0) > 0
    ) {
      deferredHydrateRef.current = true;
      return;
    }
    // Server-derived state — the persisted truth.
    const server = serverSelections(q, editable);
    const serverDrafts = server.lines;
    const serverSnapshot = persistableSnapshot(server);
    const serverBase = draftBaseSnapshot(server);

    draftBaselineRef.current = { updated_at: q.updated_at, editable, snapshot: serverBase };
    setDraftsQuoteId(q.id);

    // The server's copy as the autosave's baseline. A different quote than the
    // one hydrated last also starts its status line afresh.
    const firstHydrate = hydratedQuoteIdRef.current !== q.id;
    hydratedQuoteIdRef.current = q.id;
    if (firstHydrate) setAutosaveStatus('idle');

    // Overlay the local draft of unsaved edits, but ONLY when the server's
    // selections are still the ones it was built on and the quote is still
    // editable. A save that moved them (another tab, another device) makes the
    // draft stale, so it is discarded and the server wins. A write that moved
    // only updated_at does not: the price snapshot, a status sync and the Nexus
    // link stamp all bump it, and discarding the draft for one of those reverted
    // whatever the customer picked since the last save to what that save sent.
    // This is also what restores edits across a window-focus refetch,
    // not just a full reload. Selections only — prices always come from the
    // server snapshot. See quoteDraft.ts.
    const draft = editable ? readQuoteDraft(q.id) : null;
    if (!draft || !draftIsCurrent(draft, q.updated_at, serverBase)) {
      // None, stale, or not editable — server wins; drop a stale draft so it
      // can't resurface against a later (matching) load.
      if (draft) clearQuoteDraft(q.id);
      lastSavedSnapshotRef.current = serverSnapshot;
      failedSnapshotRef.current = null;
      setDrafts(serverDrafts);
      setTitle(server.title);
      setFinishDate(server.finishDate);
      setExpediteRequested(server.expediteRequested);
      setShipTos(server.shipTos);
      setProofQuantity(server.proofQuantity);
      setProofShipTos(server.proofShipTos);
      setOrderServices(server.orderServices);
      return;
    }

    // Apply the draft. Lines come wholesale from it (preserving any local
    // add / remove / reorder / field edit made since the last save), each
    // inheriting its server price snapshot by id where one still exists (display
    // continuity only — the editor never sends these figures back).
    const serverById = new Map(serverDrafts.map((d) => [d.local_id, d]));
    const mergedDrafts: LineDraft[] = draft.lines.map((dl) => {
      const server = serverById.get(dl.local_id);
      // An older draft carries "art to follow" as one quote-level boolean beside
      // its lines. Read it only to seed this line's per-slot marks: the server's
      // marks when it has them, else every slot the line can mark that has no
      // file on it yet.
      const legacyPending =
        draft.artwork_pending === true && !(ARTWORK_PENDING_ROLES_CONFIG_KEY in dl.config);
      return {
        local_id: dl.local_id,
        title: dl.title,
        quantity: dl.quantity,
        config: legacyPending ? withLegacyArtworkPending(dl.config, server?.config) : dl.config,
        unit_price: server?.unit_price ?? null,
        line_price: server?.line_price ?? null,
      };
    });
    const overlay: PersistableSelections = {
      lines: mergedDrafts.length > 0 ? mergedDrafts : serverDrafts,
      title: draft.project_title,
      finishDate: draft.requested_finish_date,
      expediteRequested: draft.expedite_requested ?? q.expedite_requested,
      // A draft written before split shipping existed carries only the single
      // `ship_to`, so fall back to the server list rather than collapsing a saved
      // split back to one address.
      shipTos: draft.ship_tos && draft.ship_tos.length > 0 ? draft.ship_tos : server.shipTos,
      // A draft written before the printed-sample capture existed carries neither
      // value, so the server's answer stands rather than being cleared by an older
      // draft that never had one.
      proofQuantity: draft.proof_quantity ?? q.proof_quantity,
      proofShipTos:
        draft.proof_ship_tos && draft.proof_ship_tos.length > 0
          ? draft.proof_ship_tos
          : server.proofShipTos,
      orderServices: draft.order_services,
    };
    // The autosave's baseline on a first open. On a plain draft it is the
    // server's copy, so edits restored from a draft no save ever carried are
    // dirty and go out on the customer's next action. Past that (a priced quote,
    // a rep-unlocked order) a lines save clears the price or re-sends the order
    // to Nexus, and the restored draft also holds whatever the form normalized on
    // a previous visit, so it is taken as the baseline and goes out with the
    // customer's next real edit or Save. A later overlay is the refetch after a
    // save, whose baseline (what it sent) is the one that holds.
    if (firstHydrate) {
      lastSavedSnapshotRef.current =
        q.status === 'draft' ? serverSnapshot : persistableSnapshot(overlay);
      failedSnapshotRef.current = null;
    }
    setDrafts(overlay.lines);
    setTitle(overlay.title);
    setFinishDate(overlay.finishDate);
    setExpediteRequested(overlay.expediteRequested);
    setShipTos(overlay.shipTos);
    setProofQuantity(overlay.proofQuantity);
    setProofShipTos(overlay.proofShipTos);
    setOrderServices(overlay.orderServices);
  }, [quoteQuery.data, repUnlocked, hydrateNudge]);

  // Coming back into an order opens it folded: every saved item the price
  // pre-flight finds complete starts as its one-line recap, so a multi-item order
  // reads as a list rather than a wall of configurators. An item still missing a
  // field or a required upload stays open — folding it would hide what is still
  // waiting for an answer, and its recap carries no flag until a Price or Save —
  // and so does an unsaved one, which has no header chevron to reopen it. Waits
  // for the asset and media-content reads so the upload half is judged too. Once
  // per order per mount, so a refetch never refolds what the customer opened.
  // Declared before the door arrival below so its reveal lands after this fold.
  const foldedOnLoadRef = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !drafts || draftsQuoteId !== id || !schemaQuery.data) return;
    if (assetsQuery.isPending || mediaContentQuery.isPending) return;
    if (foldedOnLoadRef.current === id) return;
    foldedOnLoadRef.current = id;
    const unfinished = new Set(
      collectConfigMisses(schemaQuery.data, drafts, presentRoles, mediaContentQuery.data ?? null).map(
        (m) => m.line_local_id
      )
    );
    setCollapsedLines(
      new Set(
        drafts
          .filter((d) => !isUnsavedLine(d.local_id) && !unfinished.has(d.local_id))
          .map((d) => d.local_id)
      )
    );
  }, [
    id,
    drafts,
    draftsQuoteId,
    schemaQuery.data,
    assetsQuery.isPending,
    mediaContentQuery.isPending,
    presentRoles,
    mediaContentQuery.data,
  ]);

  // A door arrival lands on the order exactly as the partner sees it on opening it
  // themselves, with the door's item selected: the first item lineServesDoor
  // matches, opened and brought into view the way "Edit item N" does. That covers
  // Nexus's link to the deal's linked order and the entry screen resuming the
  // deal's one open order (ADR-017). Once per order per mount, after the items
  // have painted, so a window-focus refetch never moves the page out from under
  // someone mid-edit. Never a write: an order holding no item of that door lands
  // unselected, because what an order contains is the customer's answer and a rep
  // arriving through a division is not one (ADR-015 §4). Holds for the same reads
  // the load fold waits on, so the fold can never land after this reveal and
  // close the item it opened.
  const arrivalLandedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !arrivalDoor || !drafts || draftsQuoteId !== id || !schemaQuery.data) return;
    if (assetsQuery.isPending || mediaContentQuery.isPending) return;
    if (arrivalLandedRef.current === id) return;
    arrivalLandedRef.current = id;
    const index = drafts.findIndex((d) => lineServesDoor(arrivalDoor, d.config));
    const target = drafts[index];
    if (target) revealLine(target.local_id, index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    arrivalDoor,
    drafts,
    draftsQuoteId,
    schemaQuery.data,
    assetsQuery.isPending,
    mediaContentQuery.isPending,
  ]);

  // Keep the Need-By on the family-aware STANDARD default as the product mix
  // changes. Adding a hard-bound project (a longer production turnaround) should
  // move the standard Need-By out — otherwise a date that WAS standard would read
  // as an accidental rush against the longer window. We bump ONLY when the date
  // is sitting exactly on the plain ~1-week default (an untouched standard-lane
  // date for THIS session's "today"), so a deliberate rush, a custom later date,
  // and a quote loaded with its own saved date are all left untouched — and we
  // never pull a date IN (a later date is always no-fee). This only keeps the
  // displayed Standard date honest against the family window; the expedite
  // intent is the explicit expedite_requested flag, not this date.
  useEffect(() => {
    if (!drafts) return;
    const today = todayLocalIso();
    const familyDays = turnaroundDaysForProductTypes(drafts.map((d) => lineProductType(d)));
    const familyStd = defaultNeedByDate(today, familyDays);
    const plainStd = defaultNeedByDate(today);
    setFinishDate((cur) => {
      if (!cur) return familyStd;
      if (cur === plainStd && familyStd > cur) return familyStd;
      return cur;
    });
  }, [drafts]);

  // Mirror the customer's in-progress selections to the local draft on every
  // change, so a reload / navigate-away / window-focus refetch never loses them
  // (the backend save runs on the customer's actions — the autosave below, or
  // Save / See my price / Add project — never per keystroke).
  // Selections only — never prices (CLAUDE.md §Caching). Stamped with the loaded
  // quote's version via draftBaselineRef so a server save that moved the
  // selections supersedes it on the next load. Only editable quotes write. See
  // quoteDraft.ts.
  useEffect(() => {
    const baseline = draftBaselineRef.current;
    if (!id || drafts === null || !baseline || !baseline.editable) return;
    writeQuoteDraft(id, {
      v: 1,
      base_updated_at: baseline.updated_at,
      base_snapshot: baseline.snapshot,
      saved_at: Date.now(),
      project_title: title,
      requested_finish_date: finishDate,
      expedite_requested: expediteRequested,
      ship_tos: shipTos,
      proof_quantity: proofQuantity,
      proof_ship_tos: proofShipTos,
      order_services: orderServices,
      lines: drafts.map((d) => ({
        local_id: d.local_id,
        title: d.title,
        quantity: d.quantity,
        config: d.config,
      })),
    });
    // draftBaselineRef is a ref, not a reactive dep; the selection state is what
    // should trigger a re-write.
  }, [
    drafts,
    title,
    finishDate,
    expediteRequested,
    shipTos,
    proofQuantity,
    proofShipTos,
    orderServices,
    id,
  ]);

  // The order quantity the per-address piece counts have to add up to when the
  // order ships to more than one place, or null when the order has no single
  // correct total (its configured lines state different quantities). Read from
  // the SAME `draftQuantity` the save mapping spends, through the shared resolver
  // the backend gate also calls. A unit count — nothing price-shaped.
  //
  // CONFIGURED lines only, exactly as the gate filters them. A placeholder line
  // (the seed row every new quote carries, `config: {}`) states a quantity that is
  // not part of the order, so counting it makes the totals disagree — the client
  // sees two unequal quantities, gives up on the sum, and lets a split through
  // that the gate then refuses with nothing on screen having warned about it.
  const splitOrderQuantity = splitBaseQuantity(
    (drafts ?? []).filter((d) => isConfiguredLine(d.config)).map(draftQuantity),
  );

  // The quote's resolved proof intent, read through the SAME shared resolver the
  // backend gate and both feeds use. Computed here, above the first hook that
  // needs it, so the printed-sample questions are asked and re-checked from one
  // answer rather than from a second reading of the lines further down.
  const proofTypeResolved = resolveQuoteProofType((drafts ?? []).map((d) => d.config));

  // Once we've surfaced a missing-field summary from a CLIENT-side check,
  // recompute its per-line misses live so they deflate as the customer fixes
  // fields and uploads files. Runs for the 'preflight' AND 'save' sources, and
  // touches only the misses collectConfigMisses produced (unscoped, carrying a
  // `reason`): a quote-level miss (an unanswered delivery mode, an address) and a
  // field the server rejected are not that check's to judge, so they stay as the
  // click reported them. Deflate-only: a miss the click never reported is not
  // added while the customer types, and a surviving one takes the fresh `reason`
  // so its inline message follows a missing → invalid_value flip. Server-side
  // rejections ('pricing'/'submit') clear on the next button click instead.
  useEffect(() => {
    if (!fieldSummary || (fieldSummary.source !== 'preflight' && fieldSummary.source !== 'save'))
      return;
    if (!schemaQuery.data || !drafts) return;
    const shown = fieldSummary.misses;
    const rederivable = (m: PreflightMiss): boolean => !m.scope && m.reason !== undefined;
    if (!shown.some(rederivable)) return;
    const freshByKey = new Map(
      collectConfigMisses(schemaQuery.data, drafts, presentRoles, mediaContentQuery.data ?? null).map(
        (m) => [lineFieldKey(m.line_local_id, m.field_key), m] as const
      )
    );
    const next = shown.flatMap((m) => {
      if (!rederivable(m)) return [m];
      const fresh = freshByKey.get(lineFieldKey(m.line_local_id, m.field_key));
      return fresh ? [fresh] : [];
    });
    const unchanged =
      next.length === shown.length &&
      next.every((m, i) => {
        const prev = shown[i]!;
        return (
          m.field_key === prev.field_key &&
          m.line_local_id === prev.line_local_id &&
          m.line_index === prev.line_index &&
          m.reason === prev.reason
        );
      });
    if (unchanged) return;
    setFieldSummary(next.length === 0 ? null : { ...fieldSummary, misses: next });
    // Only the inline errors of the re-derived misses move, matched on the same
    // line-namespaced key (lineFieldKey): a filled field's error clears, a
    // surviving one is re-worded from its fresh reason, and every other entry —
    // the delivery-mode error included — is left as the click set it.
    const rederivedKeys = new Set(
      shown.filter(rederivable).map((m) => lineFieldKey(m.line_local_id, m.field_key))
    );
    setFieldErrors((cur) => {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(cur)) {
        if (!rederivedKeys.has(k)) {
          out[k] = v;
          continue;
        }
        const fresh = freshByKey.get(k);
        if (fresh) out[k] = missInlineMessage(fresh);
      }
      return out;
    });
    // presentRoles is a dep so the summary deflates the moment a required file
    // lands (an upload → assetsQuery.refetch → presentRoles changes).
    // drafts carries each slot's "art to follow" mark, so the artwork misses
    // clear the moment the customer ticks one.
  }, [drafts, schemaQuery.data, fieldSummary, presentRoles, mediaContentQuery.data]);

  // Ship-to misses re-derive live as the address list changes. Removing an
  // address RE-INDEXES the ones after it, so a miss reported against
  // `ship_to.2.city` would otherwise keep pointing at a control that has moved:
  // the red box would sit on the wrong address, or on none at all, and the
  // summary link would scroll nowhere. Unlike the per-line misses above this runs
  // for a 'submit'-sourced summary too, because the shared validator re-derives
  // the answer locally without a round trip.
  //
  // Only ever RE-KEYS a summary that is already showing ship-to misses — one the
  // customer raised by asking for the order to be placed. Answering an unanswered
  // mode with Shipping can therefore swap the mode miss for the address misses the
  // gate would report next, which is the same list a re-submit would show.
  //
  // A 'save'-sourced summary only ever deflates: Save reports an unanswered mode
  // and nothing else here, so picking Shipping clears that miss rather than
  // swapping in address misses a Save never asked for.
  useEffect(() => {
    if (!fieldSummary) return;
    const shown = fieldSummary.misses.filter((m) => m.scope === 'ship_to');
    if (shown.length === 0) return;
    const shownKeys = new Set(shown.map((m) => m.field_key));
    const derived = [
      ...shipToMissingFieldKeys(shipTos, {
        services: orderServices,
        orderQuantity: splitOrderQuantity,
      }),
      // The printed samples' misses re-key for the same reason: removing a
      // sample address re-indexes the ones after it. They share the quote-level
      // 'ship_to' scope (shipToFieldLabel resolves their keys), so recomputing
      // only the order's half here would silently drop them from the summary.
      ...proofDeliveryMissingFieldKeys({
        proof_type: proofTypeResolved,
        proof_quantity: proofQuantity,
        proof_ship_tos: proofShipTos,
      }),
    ];
    const fresh =
      fieldSummary.source === 'save' ? derived.filter((k) => shownKeys.has(k)) : derived;
    const freshKeys = new Set(fresh);
    const unchanged =
      shown.length === fresh.length && shown.every((m) => freshKeys.has(m.field_key));
    if (unchanged) return;

    const others = fieldSummary.misses.filter((m) => m.scope !== 'ship_to');
    const nextMisses: PreflightMiss[] = [
      ...others,
      ...fresh.map((key) => ({
        field_key: key,
        field_label: shipToFieldLabel(key) ?? key,
        line_local_id: '__ship_to__',
        line_index: 0,
        scope: 'ship_to' as const,
      })),
    ];
    setFieldSummary(nextMisses.length === 0 ? null : { ...fieldSummary, misses: nextMisses });
    setFieldErrors((cur) => {
      // Carry the message the summary was already showing rather than inventing a
      // second wording for the same failure.
      //
      // Every key the recompute above can PRODUCE has to be cleared here, or a
      // stale one survives as a red box on a control the fresh list no longer
      // names — so the printed samples' two namespaces are dropped alongside the
      // order's.
      const isDestinationKey = (k: string): boolean =>
        k.startsWith(`${SHIP_TO_FIELD_PREFIX}.`) ||
        k.startsWith(`${PROOF_SHIP_TO_FIELD_PREFIX}.`) ||
        k === PROOF_QUANTITY_FIELD_KEY;
      const carried = Object.entries(cur).find(([k]) => isDestinationKey(k))?.[1];
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(cur)) {
        if (!isDestinationKey(k)) next[k] = v;
      }
      for (const key of fresh) next[key] = carried ?? t('edit.fillHighlighted');
      return next;
    });
  }, [
    shipTos,
    orderServices,
    splitOrderQuantity,
    proofTypeResolved,
    proofQuantity,
    proofShipTos,
    fieldSummary,
    t,
  ]);

  // (v4: the fulfillment-line auto-quantity sync was removed — fulfillment is no
  // longer a per-line product type but a quote-level order service.)

  // The status read that first finds a change order closed is the one that
  // settles the edited order's total on the backend, and it runs beside the
  // quote fetch rather than before it. So a placed order is refetched once after
  // its first status read on this page, and again whenever a change order is
  // seen to close while the page is open.
  const hadChangeOrderRef = useRef<boolean | null>(null);
  useEffect(() => {
    const st = statusQuery.data?.ok ? statusQuery.data.status : null;
    if (!st || !quoteQuery.data) return;
    const open = Boolean(st.change_order);
    const firstRead = hadChangeOrderRef.current === null;
    const placed = isChangeOrderEditableStatus(quoteQuery.data.status);
    if ((firstRead && placed && !open) || (hadChangeOrderRef.current === true && !open)) {
      void quoteQuery.refetch();
    }
    hadChangeOrderRef.current = open;
    // quoteQuery is stable; depend on the data snapshots that gate the refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.data, quoteQuery.data]);

  // When Nexus owns the quote AND has approved it, the backend has synced
  // the local snapshot (status='approved', authoritative total). Refresh the
  // quote once so the header tag + the checkout page see the new state. Only
  // for control='sales' — a control='portal' approval does NOT flip local
  // status, so guarding on it here also prevents a refetch loop.
  useEffect(() => {
    const st = statusQuery.data?.ok ? statusQuery.data.status : null;
    if (
      st?.control === 'sales' &&
      st.approved_quote &&
      quoteQuery.data &&
      quoteQuery.data.status !== 'approved'
    ) {
      void quoteQuery.refetch();
    }
    // quoteQuery is stable; depend on the data snapshots that gate the refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.data, quoteQuery.data]);

  // On-load "needs review" list for an imported PAST order (is_historical) or a
  // quote flagged partial at import. Derived live from the SAME shared validators
  // the price gate uses (collectConfigMisses → human field labels), so the
  // partial banner tells the partner exactly which details to fill in — a real
  // field name like "How many do you need?", never a raw import key — and it
  // deflates as they fix each one. Empty for a fresh/portal-originated draft (we
  // don't nag a blank new order) and for a locked/approved quote.
  const importReview = useMemo<PreflightMiss[]>(() => {
    const q = quoteQuery.data;
    const sch = schemaQuery.data;
    if (!q || !sch || !drafts) return [];
    const editable = q.status === 'draft' || q.status === 'priced' || repUnlocked;
    if (!editable || !(q.is_historical || q.is_partial)) return [];
    // Read the persisted flag off the loaded quote — an art-to-follow import
    // shouldn't nag about the missing cover/manuscript in its review list.
    return collectConfigMisses(
      sch,
      drafts,
      presentRoles,
      mediaContentQuery.data ?? null,
    );
  }, [quoteQuery.data, schemaQuery.data, drafts, presentRoles, mediaContentQuery.data, repUnlocked]);

  // Group the review misses by field so N projects missing the SAME field — the
  // common historical case, where every line's product type failed to map —
  // collapse to ONE line with compact numbered jump-links, instead of N nearly
  // identical sentences that read like N separate failures. Quote-level misses
  // (ship_to / order_services) group by scope and never carry a project number.
  const importReviewGroups = useMemo(() => {
    const groups: {
      key: string;
      field_label: string;
      scope?: PreflightMiss['scope'];
      misses: PreflightMiss[];
    }[] = [];
    const byKey = new Map<string, number>();
    for (const m of importReview) {
      // Group only the SAME field repeated across projects. Include scope in the
      // key so distinct quote-level fields (ship_to.city vs ship_to.zip — same
      // scope, different field) stay separate rows, not one collapsed group.
      const gkey = `${m.scope ?? ''}::${m.field_key}`;
      let gi = byKey.get(gkey);
      if (gi === undefined) {
        gi = groups.length;
        byKey.set(gkey, gi);
        groups.push({ key: gkey, field_label: m.field_label, scope: m.scope, misses: [] });
      }
      groups[gi]!.misses.push(m);
    }
    return groups;
  }, [importReview]);

  // How the intro reads scales with severity. The worst (and common) case for a
  // historical clone is the PRODUCT TYPE itself not mapping — re-picking it is a
  // real task, not a "quick look", so we say so plainly rather than undercut it
  // with "a few details". Minor gaps keep the reassuring wording.
  const productTypeMissingCount = useMemo(
    () => importReview.filter((m) => m.field_key === 'product_type').length,
    [importReview],
  );

  // Release v1 pricing lockdown (env PRICING_LOCKED, served via /api/config).
  // When locked, the portal shows NO online price anywhere — the "See my price"
  // / "Place my order" actions, the live PricingBlock, and the saved-price
  // snapshot are all suppressed and replaced by a contact-sales handoff. The
  // backend enforces this authoritatively; this flag drives the UX. Read here,
  // with the other hooks, so the hook call precedes the early returns below.
  const pricingLocked = usePricingLocked();

  // A save asked for by a control whose own write is a state update this render
  // has not committed yet — the kit-contents list, whose new row is not
  // persistable on its own (see handleSave's re-stamp) — or by the
  // autosave, on the customer's own edit. Bumping the counter batches with that
  // write, so the effect's handleSave closure reads the snapshot the customer
  // just changed. `saving` coalesces: a request arriving mid-flight is held and
  // runs once the in-flight save clears, rather than racing it with a body built
  // before the other's row existed.
  const [saveRequestSeq, setSaveRequestSeq] = useState(0);
  // Both start at 0, which is what keeps a save from firing on mount.
  const savedRequestSeqRef = useRef(0);
  // Whether the batch the queue has not run yet holds a control's request
  // (requestSave) as well as autosave ones. Any one keeps the batch's failure
  // loud — the kit list's save reports its errors as it always has — and only a
  // batch made purely of autosave requests runs quiet.
  const controlRequestPendingRef = useRef(false);
  // handleSave is re-created every render and reads live state, so it is held by
  // ref rather than depended on — the counter and the in-flight flag are the
  // triggers.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  // The route's quote as of the latest render, so a save still running from an
  // earlier render can tell that the editor has moved on to another quote.
  const routeIdRef = useRef(id);
  routeIdRef.current = id;
  // Every save waits for the one before it to settle, so two PATCHes of this
  // quote are never in flight together and the later answer always lands last.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  // Whether the last save to land was refused or never reached the server, so
  // the server holds an older version of this order than the page shows.
  const lastSaveFailedRef = useRef(false);
  function requestSave(): void {
    controlRequestPendingRef.current = true;
    setSaveRequestSeq((n) => n + 1);
  }
  // The save in front of a new USB / disc content upload. The session route
  // checks the STORED delivery choice, and the one on screen may not be stored
  // yet: a rep's edits wait for their own Save, and a customer's autosave may
  // still be in flight. Skipped when the stored line already says "Upload them
  // here", so a rep's upload does not re-import the order into Nexus for nothing.
  function persistMediaUploadChoice(lineId: string): Promise<boolean> {
    const stored = quoteQuery.data?.lines.find((l) => l.id === lineId);
    if (readMediaContentDelivery(stored?.config) === 'upload') return Promise.resolve(true);
    // The same hold the autosave observes: a lines PATCH landing inside a price
    // or an order placement moves the line set out from under that request.
    if (pricingState.kind === 'pricing' || submitState.kind === 'submitting') {
      return Promise.resolve(false);
    }
    return handleSave({ silent: true });
  }
  // Whether a save of the loaded quote may run now. Held while a price or an
  // order placement is in flight: a lines PATCH landing inside either would
  // move the line set out from under the request reading it.
  const autosaveReady =
    Boolean(id) &&
    drafts !== null &&
    draftsQuoteId === id &&
    isEditable &&
    pricingState.kind !== 'pricing' &&
    submitState.kind !== 'submitting';
  useEffect(() => {
    if (!autosaveReady || saving || autosaveStatus === 'saving') return;
    if (savedRequestSeqRef.current === saveRequestSeq) return;
    savedRequestSeqRef.current = saveRequestSeq;
    const fromControl = controlRequestPendingRef.current;
    controlRequestPendingRef.current = false;
    // No autosave for an Argo rep: under impersonation every save re-imports the
    // order into its Nexus Opportunity (syncQuoteToOpportunityOnSave), and on a
    // change order the partner re-signs there, so a save per field would churn
    // both. The rep's edits stay in the local draft until their own Save.
    if (!fromControl && impersonating) return;
    // An autosave request skips itself inside handleSave when nothing changed
    // since the last save, so a click or a focus change with no edit sends nothing.
    void handleSaveRef.current({ silent: true, auto: !fromControl });
  }, [saveRequestSeq, saving, autosaveStatus, autosaveReady, impersonating]);

  // The autosave flush for the moments the queue cannot wait for a render: the
  // tab hidden, the page unloading, the editor unmounting or moving to another
  // quote. Re-pointed after every commit, so a cleanup reads the PREVIOUS
  // commit's closure (React runs every effect cleanup before any new effect),
  // which still holds the quote being left and its selections.
  const autosaveFlushRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    autosaveFlushRef.current = () => {
      if (autosaveReady && !impersonating) {
        void handleSave({ silent: true, auto: true });
      }
    };
  });

  // Whether the customer typed or clicked inside the editor since the last
  // commit. Set by the document listener below; cleared after every commit.
  const customerEventPendingRef = useRef(false);
  // The selections as of the previous commit, in persistableSnapshot terms.
  const committedSnapshotRef = useRef<string | null>(null);
  // A selection change the customer did not make — a SchemaForm default seeded
  // on mount, a legacy value remapped, an auto-fill from an uploaded file, the
  // Need-By kept on the standard date — is folded into the baseline while
  // nothing is unsaved, so it never makes the quote read as dirty on its own
  // (on a priced quote, a lines save it caused would clear the price). It still
  // goes out with the customer's next real edit. A change following a customer
  // event, or landing on top of unsaved edits, stays dirty.
  useEffect(() => {
    if (drafts === null) return;
    const snapshot = persistableSnapshot({
      title,
      finishDate,
      expediteRequested,
      shipTos,
      proofQuantity,
      proofShipTos,
      orderServices,
      lines: drafts,
    });
    const previous = committedSnapshotRef.current;
    committedSnapshotRef.current = snapshot;
    if (customerEventPendingRef.current) return;
    if (previous === lastSavedSnapshotRef.current) lastSavedSnapshotRef.current = snapshot;
  }, [drafts, title, finishDate, expediteRequested, shipTos, proofQuantity, proofShipTos, orderServices]);
  // Declared after the effect above so it runs after it in every commit.
  useEffect(() => {
    customerEventPendingRef.current = false;
  });

  // The autosave's triggers are the customer's own actions inside the editor,
  // never a timer: a committed change (a text field's `change` fires on leaving
  // it edited; a select, checkbox or radio fires at once), focus leaving a
  // control, and any click. Listened for on `document` in the bubble phase, so
  // React's handlers on its root container have already run and the state they
  // set commits in the same batch as the request. Clicks and focus changes that
  // edited nothing are dropped by handleSave's dirty test.
  const editorRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const inEditor = (event: Event): boolean => {
      const root = editorRootRef.current;
      return Boolean(root && event.target instanceof Node && root.contains(event.target));
    };
    const onAction = (event: Event): void => {
      if (!inEditor(event)) return;
      setSaveRequestSeq((n) => n + 1);
    };
    // Marks the change about to commit as the customer's. Capture phase, so it
    // runs before React's own handler: a real event's update commits in the
    // microtask after React's root listener, before any bubble listener on
    // `document` runs, and the re-baseline effect would otherwise fold the edit
    // in as if nobody had made it.
    const onCustomerEvent = (event: Event): void => {
      if (!inEditor(event)) return;
      customerEventPendingRef.current = true;
    };
    // Typing retires a stale "All changes saved"; the save itself waits for the
    // field's `change` or for focus to leave.
    const onInput = (event: Event): void => {
      if (!inEditor(event)) return;
      setAutosaveStatus((s) => (s === 'saved' ? 'idle' : s));
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') autosaveFlushRef.current();
    };
    // Best effort: an unloading page may cancel the request, and the local draft
    // the mirror effect keeps is what carries those edits to the next visit.
    const onPageHide = (): void => autosaveFlushRef.current();
    document.addEventListener('input', onCustomerEvent, true);
    document.addEventListener('change', onCustomerEvent, true);
    document.addEventListener('click', onCustomerEvent, true);
    document.addEventListener('input', onInput);
    document.addEventListener('change', onAction);
    document.addEventListener('focusout', onAction);
    document.addEventListener('click', onAction);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('input', onCustomerEvent, true);
      document.removeEventListener('change', onCustomerEvent, true);
      document.removeEventListener('click', onCustomerEvent, true);
      document.removeEventListener('input', onInput);
      document.removeEventListener('change', onAction);
      document.removeEventListener('focusout', onAction);
      document.removeEventListener('click', onAction);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      // Leaving this quote (unmount, or the route's :id moving on): unsaved
      // edits go out now, against the quote they were made on.
      autosaveFlushRef.current();
    };
  }, [id]);

  if (!id) return <p>{t('edit.missingQuoteId')}</p>;

  // Check the quote error BEFORE the loading gate. `drafts` is only set from a
  // successful quote fetch, so a failed fetch leaves `!drafts` true forever —
  // which would otherwise pin the page on "Loading…" and mask the real error
  // (e.g. a backend 500 from schema drift).
  if (quoteQuery.error) {
    return <div className="banner banner--error">{t('edit.error.loadFailed')}</div>;
  }
  if (quoteQuery.isLoading || schemaQuery.isLoading || !drafts) {
    return <p>{t('common:states.loading')}</p>;
  }
  if (schemaQuery.error || !schemaQuery.data) {
    return (
      <div className="banner banner--error">
        {t('edit.error.schemaNotReady')}
      </div>
    );
  }

  const quote = quoteQuery.data!;
  const nexusQuoteNumber = boundNexusQuoteNumber(quote, quoteStatus);
  const schema = schemaQuery.data;
  // group_key → grain, for routing a backend field-keyed failure to the surface
  // that actually renders the field (see applyFieldKeyFailure).
  const schemaGroupScope = groupScopeByKey(schema);

  // Normal pricing gate: a sales-controlled quote is priced by the rep, so the
  // portal doesn't offer "See my price"/"Place my order" for it. A change order
  // re-opens pricing for the rep: the order being changed needs a new price.
  const pricingAllowed = !salesControlled || changeOrderOpen;
  // Under the release-wide pricing lockdown the customer STILL sees a "See my
  // price" button — clicking it just yields the contact-sales handoff (the
  // backend returns CANNOT_PRICE, PricingBlock renders the "contact a sales rep"
  // copy) instead of a price. The lock supersedes the sales-controlled gate, so
  // the button shows even then. Ordering ("Place my order") stays off.
  const showSeeMyPrice = pricingAllowed || pricingLocked;
  // The last pricing attempt came back CANNOT_PRICE — this needs a custom
  // (hand-quoted) price, not an auto-price retry. Re-showing "See my price"
  // here would just repeat the same contact-sales outcome, so the action
  // button below relabels to "Submit for quote" — a distinct, honest next
  // step — instead of leaving a price-check button live with nothing behind
  // it. Per product/Nexus-review feedback: never hide the button here.
  const cannotPriceFailure =
    pricingState.kind === 'priced' &&
    !pricingState.response.ok &&
    pricingState.response.failure.code === 'CANNOT_PRICE';

  // Who this page names in its own contact lines. A live pricing FAILURE carries
  // the rep Nexus resolved for the account and is the freshest answer; outside
  // one, the snapshot the last successful price persisted on the quote says the
  // same thing. Both fall back to the shared inbox, so no branch here has to ask
  // whether a real person was resolved (FB-0026).
  const contactSalesRep =
    pricingState.kind === 'priced' && !pricingState.response.ok
      ? salesRepFor(pricingState.response.failure)
      : quote.sales_rep_name && quote.sales_rep_email
        ? { name: quote.sales_rep_name, email: quote.sales_rep_email }
        : DEFAULT_SALES_REP;

  // v1 launch posture: while pricing is locked, no online price is surfaced —
  // so a "See my price" CTA would promise a number that never comes, and the
  // primary action reads "Submit for quote" in every state instead. This is
  // driven by the SAME runtime pricing-lockdown flag (PRICING_LOCKED env →
  // /api/config → usePricingLocked) that gates every other price surface, so it
  // toggles off and on with no rebuild: flip PRICING_LOCKED off (when pricing
  // opens, ~within a month) and the label automatically reverts to the normal
  // "See my price" → "Submit for quote" (post-CANNOT_PRICE) behaviour. All of
  // that logic (cannotPriceFailure above, the button ternary below) is intact.
  // Shared by the action button and the reprice banner so the two never differ.
  const primaryPriceCtaLabel =
    pricingLocked || cannotPriceFailure ? t('edit.cta.submitForQuote') : t('edit.cta.seePrice');

  // Step 10: when the quote has a BOUND book line, nudge the customer that a
  // loose insert is its OWN project (not a checkbox on the book). The schema
  // mirrors this on the book's Finishing step (info tip on `finishing`); this
  // sits beside the actual "Add another project" control so the guidance is
  // right where the customer acts on it. Derived from the shared
  // BOOKLET_PRODUCT_TYPES set (no hardcoded family list) minus loose_leaf — the
  // customer assembles those drilled sheets in their own 3-ring binder, so
  // there's nothing for us to "bind in." Mirrors the schema tip's coverFamilies
  // scope (saddle-stitch, perfect-bound, coil/wire-o).
  const hasBoundBookLine = drafts.some((d) => {
    const pt = lineProductType(d);
    return pt !== undefined && pt !== 'loose_leaf' && BOOKLET_PRODUCT_TYPES.has(pt);
  });

  // Every item on the order, for the fulfillment section: each one is stocked
  // under its own SKU, so the section gives each a row. Labelled the way the item
  // list labels them, with the review row's "Item 2 · Paperback book" caption.
  // `id` is the draft's local_id, which IS the QuoteLine id once saved — the id
  // the submit gate names in its `fulfillment.target.<id>.*` keys, and the id a
  // kit's `quote_line` contents row points at.
  //
  // A line with no product picked yet has captured nothing to stock, and the
  // shared gate skips it too, so it gets no row.
  const fulfillmentItems: FulfillmentItem[] = drafts.flatMap((d, idx) => {
    if (!isConfiguredLine(d.config)) return [];
    const named = d.title || deriveProjectName(schema, d.config) || '';
    const itemN = t('edit.projectN', { n: idx + 1 });
    const family = familyLabel((d.config.product_type as FamilyCode | undefined) ?? null);
    return [
      {
        id: d.local_id,
        saved: !isUnsavedLine(d.local_id),
        label: named || itemN,
        caption: named ? `${itemN} · ${family}` : family,
        config: d.config,
        title: d.title,
        ...(named ? { suggestedSkuName: named } : {}),
      },
    ];
  });

  function fulfillmentTargetItemFor(key: string): FulfillmentItem | undefined {
    const parsed = parseFulfillmentTargetFieldKey(key);
    return parsed ? fulfillmentItems.find((item) => item.id === parsed.lineId) : undefined;
  }

  function patchLineFulfillmentTarget(
    localId: string,
    target: LineFulfillmentTarget | undefined,
  ): void {
    setDrafts((cur) =>
      (cur ?? []).map((d) =>
        d.local_id === localId ? { ...d, config: withLineFulfillmentTarget(d.config, target) } : d,
      ),
    );
  }

  // Step 11: when the quote has a HARDCOVER perfect-bound line, nudge the
  // customer that a dust jacket is its OWN project (its own size, paper, and
  // artwork) — not a field on the book. Mirrors the schema's `info_only` tip on
  // the `cover_binding_style` field; this helper sits beside the actual "Add
  // another project" control so the guidance is right where the customer acts
  // on it. Adding a project runs the SAME schema-driven configurator on a new
  // QuoteLine (the existing multi-line model — addLine), where the jacket is
  // specced + priced like any other piece.
  const hasHardcoverLine = drafts.some(isHardcoverBookLine);

  // A physical proof is a mailed printed sample — it goes back and forth by
  // carrier, so a pure self-pickup order can't carry one. When the quote
  // resolves to a physical proof (any line asked for it), the order-services
  // section forces Shipping and disables the Pickup option (see
  // OrderServicesForm). Uses the SAME shared resolver as the submit proof gate.
  const physicalProofSelected = proofTypeResolved === 'physical';

  // The quote-level "Schedule" card (NeedByPicker) renders INSIDE the first
  // project's configurator — after the Proofs section — via the SchemaForm
  // renderAfterGroup slot. It's anchored to the group that holds `proof_type`
  // (resolved from the schema, not hardcoded to `process`). The card owns the
  // Standard-vs-rush "speed presets" UX and the expedite derivation; the +10%
  // itself stays Nexus's (emitted at pricing time in services/pricing/index.ts).
  // Only the FIRST line gets the slot so the quote's single Need-By renders once.
  // Sentinel (not null) when the schema has no `proof_type` field, so a missing
  // group never collapses to SchemaForm's null ungrouped key — which would fire
  // the card after every ungrouped section instead of zero times.
  const scheduleAfterGroupKey =
    schema.fields.find((f) => f.key === 'proof_type')?.group_key ?? '__no_proof_group__';
  const todayIso = todayLocalIso();
  // The order's standard turnaround window — the longest across its projects, so
  // a hard-bound book stretches the whole quote's default Need-By + rush
  // threshold out. Passed to NeedByPicker so the Standard lane's date and the
  // "is this a rush?" line both reflect the actual product mix.
  const quoteTurnaroundDays = turnaroundDaysForProductTypes(drafts.map((d) => lineProductType(d)));
  // The quote-level Need-By card rides inside the FIRST item's configurator,
  // after its Proofs section. Two things detach it, and both fall back to the
  // standalone card above the items: a first item the customer has collapsed,
  // and a first item whose family has nothing to proof. That last one is the quiet case —
  // SchemaForm calls renderAfterGroup once per RENDERED section, and an
  // `assembly` / `something_else` / not-yet-picked item renders no Proofs
  // section, so the anchor simply never fires and the card appeared nowhere on
  // the page. Read off the same shared predicate the submit gate uses, so the
  // two can't disagree about what counts as proofable.
  const firstDraft = drafts[0];
  // A draft with no item at all is only an empty draft — every door seeds one
  // and the last item cannot be removed — but one can still load, and it has no
  // configurator to host the card either.
  const needByAnchorDetached =
    !firstDraft ||
    collapsedLines.has(firstDraft.local_id) ||
    !quoteHasProofableLine([firstDraft.config]);
  // Each "art to follow" mark's only control is the checkbox on its own upload
  // slot — so a marked line is reachable only while it is showing slots. A
  // collapsed item hides them, a "Something else" item has no artwork block, and
  // an unsaved one shows the "save first" note instead. The lines whose marks
  // the customer cannot currently reach, for the warning's own escape hatch.
  const unreachableArtworkMarkLines = salesQuoteLocked
    ? []
    : drafts.filter(
        (d) =>
          linePendingArtworkRoles(d.config).size > 0 &&
          (collapsedLines.has(d.local_id) ||
            isSomethingElseLine(d) ||
            isUnsavedLine(d.local_id) ||
            !lineHostsArtworkPendingToggle(d.config))
      );
  const renderNeedByAfterGroup = (groupKey: string | null): ReactNode =>
    groupKey === scheduleAfterGroupKey ? (
      <NeedByPicker
        today={todayIso}
        turnaroundDays={quoteTurnaroundDays}
        value={finishDate}
        onChange={setFinishDate}
        expedited={expediteRequested}
        onExpeditedChange={setExpediteRequested}
        itemCount={drafts.length}
        // Nested inside item 1's configurator: the section panel comes off and
        // the title shrinks, so a date covering every item does not read as a
        // step of the item hosting it.
        variant="embedded"
        disabled={!isEditable}
      />
    ) : null;

  // The last project cannot be removed: an order with no projects is not an
  // order, and Remove would strand the customer on a quote that prices as
  // INVALID_CONFIG with nothing on the page to fix. Kitting is an `assembly`
  // item like any other project (ADR-020), so there is no zero-item shape left
  // that still stands as an order.
  const canRemoveLine = drafts.length > 1;

  // Projects a kit contents row may point at. Only PERSISTED ones: a line added
  // this session carries a synthetic local id, and the write boundary refuses a
  // row claiming an id the quote does not own. Each option's label is the same
  // display name the project's own card shows, and it is frozen onto the row at
  // pick time.
  // Kept in PAGE ORDER, which is an R25 exception the rule's own wording covers:
  // alphabetical sorting governs data-layer catalogs (the SKU picker, the project
  // type list), and this is a positional list. A customer picks "Project 2" by
  // where it sits on the page — the fallback label is literally its index — so
  // sorting by title would leave the numbering out of step with the form above it.
  const referenceableLines: KitContentLineOption[] = drafts
    .map((d, idx) => ({
      id: d.local_id,
      label:
        d.title || deriveProjectName(schema, d.config) || t('edit.projectN', { n: idx + 1 }),
      persisted: !isUnsavedLine(d.local_id),
    }))
    .filter((o) => o.persisted)
    .map(({ id, label }) => ({ id, label }));
  const hasUnsavedLines = drafts.some((d) => isUnsavedLine(d.local_id));

  // The pre-submit review row's items, in page order (positional, like
  // referenceableLines above — the card numbering has to match the form's).
  // `missCount` is this item's share of the line-namespaced fieldErrors map, the
  // same slice its own configurator reads, so a card can never flag a count the
  // item's inline errors disagree with.
  const summaryStripItems: SummaryStripItem[] = drafts.map((d) => {
    const prefix = `${d.local_id}::`;
    return {
      local_id: d.local_id,
      title: d.title,
      config: d.config,
      quantity: draftQuantity(d),
      missCount: Object.keys(fieldErrors).filter((k) => k.startsWith(prefix)).length,
      ...(isAssemblyLine(d) ? { assembly: configAssembly(d.config) ?? {} } : {}),
    };
  });

  // Adopt the server's ownership decisions on an assembly snapshot the overlay is
  // about to re-apply. An identifier the write boundary HEALED away — a saved-kit
  // pick, or a contents row naming something this quote no longer owns — is absent
  // from what came back, and carrying the local copy forward would have the NEXT
  // save read it as a fresh claim and refuse it outright. A row the server dropped
  // only for being INCOMPLETE is the one the re-stamp exists to keep, and the
  // shared predicate is what tells the two apart. Runs over each assembly item's
  // own kit on its line config.
  function healAssembly(
    local: AssemblySnapshot,
    server: AssemblySnapshot | undefined
  ): AssemblySnapshot {
    const serverRows = Array.isArray(server?.kit_contents) ? server.kit_contents : [];
    const healedRows = new Map(serverRows.map((r) => [r.row_id, r]));
    const out: AssemblySnapshot = { ...local };
    // The snapshot only ever admits the pair together — a name with no id behind
    // it reads back as a kit the quote does not hold — so the description goes
    // wherever the id goes.
    if (local.bom_id && !server?.bom_id) {
      delete out.bom_id;
      delete out.bom_snapshot;
    }
    if (Array.isArray(local.kit_contents)) {
      out.kit_contents = local.kit_contents.flatMap((r) => {
        const kept = healedRows.get(r.row_id);
        if (kept) return [kept];
        return kitContentRowIncomplete(r) ? [r] : [];
      });
    }
    return out;
  }

  // `silent` is set when handleSave runs as the implicit pre-step of
  // "See my price" — there we don't want a "Saved" toast competing with
  // the pricing result. An explicit Save-button click shows the toast.
  // `lines` overrides the drafts to persist: addLine() passes the freshly
  // appended array because React state (drafts) hasn't flushed yet when it
  // triggers the auto-save.
  //
  // Every successful save re-stamps the local draft onto the version this PATCH
  // created (see the re-stamp below), so the refetch overlays the customer's
  // selections rather than replacing them with what the save sent. That keeps an
  // edit made during the round trip, and a control whose own row the write
  // boundary cannot accept yet: a just-added kit-contents row carries no item and
  // no description, so normalizeKitContentRow refuses it, and a refetch that won
  // would hand back a list without it. Status, is_partial and the price snapshot
  // still come from the fresh server quote.
  //
  // `auto` is the autosave: it runs only when the selections differ from the last
  // save, and it is quiet. It leaves the Save button, the "Saved" banner, the
  // error banner and the field errors alone, reporting through autosaveStatus
  // only; a failure keeps the edits in the local draft for the next attempt.
  //
  // Queued behind any save still in flight (saveChainRef), and judged against
  // the baseline when its turn comes, so an autosave asked for alongside an
  // explicit Save of the same selections sends nothing. Resolves true when the
  // order on the server now holds what this save was asked to send (persisted, or
  // nothing to send), false when it was refused or never reached the server.
  function handleSave(opts?: SaveOptions): Promise<boolean> {
    // The baselines as of the request, for a save that lands after the editor has
    // hydrated another quote: by then the live refs describe that quote, and
    // judging this one against them would PATCH a quote nobody edited.
    const requestedBaseline = {
      saved: lastSavedSnapshotRef.current,
      failed: failedSnapshotRef.current,
    };
    const run = saveChainRef.current.then(() => persistQuote(opts, requestedBaseline));
    saveChainRef.current = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  async function persistQuote(
    opts: SaveOptions | undefined,
    requestedBaseline: { saved: string | null; failed: string | null },
  ): Promise<boolean> {
    const linesToSave = opts?.lines ?? drafts!;
    const auto = opts?.auto === true;
    // What this save sends, in the dirty test's terms, from the same closure the
    // body below is built from.
    const sentSnapshot = persistableSnapshot({
      title,
      finishDate,
      expediteRequested,
      shipTos,
      proofQuantity,
      proofShipTos,
      orderServices,
      lines: linesToSave,
    });
    const onLoadedQuote = routeIdRef.current === id && hydratedQuoteIdRef.current === id;
    const baseline = onLoadedQuote
      ? { saved: lastSavedSnapshotRef.current, failed: failedSnapshotRef.current }
      : requestedBaseline;
    if (auto && (sentSnapshot === baseline.saved || sentSnapshot === baseline.failed)) {
      return true;
    }
    // A line still on a synthetic "new-…" id is minted by the save that sends it.
    // This closure can predate the refetch that remaps it (an autosave queued
    // behind Add item), and sending it again would mint a second line, so the
    // autosave leaves it to Add item's own save, or to the explicit Save.
    if (auto && linesToSave.some((d) => isUnsavedLine(d.local_id))) return true;
    // A save still landing for a quote the editor has since moved off (the flush
    // on leaving it) must not touch the baselines, the status line or the query
    // of the quote now on screen.
    const forLoadedQuote = (): boolean =>
      routeIdRef.current === id && hydratedQuoteIdRef.current === id;
    // The delivery mode is a required answer, but required by submit, not by
    // Save: like every other missing answer it is saved past and REPORTED below,
    // never refused, so the rest of the order (a media line's "Upload them here"
    // above all, which its upload reads back from the stored order) is kept.
    // Read off the shared ship-to validator — the submit gate's own source for
    // this key — so Save and submit cannot disagree about an unanswered mode.
    const modeUnanswered = shipToMissingFieldKeys(shipTos, { services: orderServices }).includes(
      DELIVERY_MODE_FIELD_KEY
    );
    if (auto) {
      if (forLoadedQuote()) setAutosaveStatus('saving');
    } else {
      setSaving(true);
      setBannerError(null);
      setSavedNotice(false);
    }
    // Counted from the PATCH until the draft is re-stamped on what it wrote. A
    // refetch of this quote landing inside that window (a window focus) holds a
    // copy the draft is not yet based on, and the load effect waits for this.
    const flightQuoteId = id!;
    const inFlight = savesInFlightRef.current;
    inFlight.set(flightQuoteId, (inFlight.get(flightQuoteId) ?? 0) + 1);
    let settled = false;
    const settleInFlight = (): void => {
      if (settled) return;
      settled = true;
      const left = (inFlight.get(flightQuoteId) ?? 1) - 1;
      if (left > 0) {
        inFlight.set(flightQuoteId, left);
        return;
      }
      inFlight.delete(flightQuoteId);
      if (deferredHydrateRef.current) {
        deferredHydrateRef.current = false;
        setHydrateNudge((n) => n + 1);
      }
    };
    try {
      const saved = await quotesApi.updateQuote(id!, {
        project_title: title || null,
        requested_finish_date: finishDate || null,
        // Explicit rush choice from the Schedule card — transmitted so Nexus
        // prices the +10% from the customer's actual pick, not a date calc.
        expedite_requested: expediteRequested,
        // Persist the delivery destination + order services alongside the rest
        // so they survive a save. (Sending `lines` here clears the price
        // snapshot unless the only line change is a fulfillment target.)
        // Where the order ships — the whole list, REPLACING what is stored, so an
        // address the customer removed is gone rather than left behind.
        ship_tos: shipTos,
        // The printed sample: how many, and where each goes. Persisted with the
        // rest so a reload does not lose them; the counts and the addresses are
        // intent, and neither is part of the price (Nexus charges for the proof).
        proof_quantity: proofQuantity,
        proof_ship_tos: proofShipTos,
        order_services: orderServices,
        lines: linesToSave.map((d, idx) => {
          // Sync direction: schema-form-edited config.quantity wins over the
          // structural QuoteLine.quantity (draftQuantity). Lets the configurator
          // — with its family-aware tips + validation — be the authoritative
          // edit surface.
          const cfgQty = draftQuantity(d);
          return {
            // Send the persisted line id so the backend updates this line
            // in place (keeping its artwork attached). New lines carry a
            // synthetic "new-…" local_id and are created server-side.
            ...(isUnsavedLine(d.local_id) ? {} : { id: d.local_id }),
            title: d.title,
            quantity: cfgQty,
            config: d.config,
            display_order: idx,
          };
        }),
      });
      if (forLoadedQuote()) {
        lastSavedSnapshotRef.current = sentSnapshot;
        failedSnapshotRef.current = null;
      }
      // Whether this quote's draft may be re-stamped: the loaded baseline's
      // answer, or this closure's own when the editor has moved to another quote.
      const editableHere = forLoadedQuote() ? draftBaselineRef.current?.editable === true : isEditable;
      if (id && editableHere) {
        // Every save re-bases the local draft on the version it just wrote, so an
        // edit made while it was in flight is not thrown away as stale when the
        // refetch lands.
        //
        // Re-stamp the draft the mirror effect last wrote, NOT a snapshot rebuilt
        // from this closure: the closure froze at the click, and a keystroke landed
        // during the round-trip would be reverted by the overlay a moment later.
        // Reading what is stored carries whatever the customer has typed since.
        //
        // The ids the PATCH just minted are adopted on the way through. The refetch
        // is normally what remaps a line's synthetic "new-…" local_id, and the
        // overlay is about to win over it, so a line added this session would
        // otherwise stay pinned to an id the upload routes refuse. Each SENT
        // synthetic id is matched to the id minted at the display_order it was sent
        // with, then found in the draft by that synthetic id rather than by its
        // position: an item added or duplicated during the round trip shifts the
        // positions, and matching by position would hand the next item's id to the
        // copy. An item the draft gained after this save was built was never sent,
        // so it keeps its synthetic id for its own save to mint. If a sent synthetic
        // id has no match, or the stored draft does not hold it yet (addLine and
        // duplicateLine send their lines before the mirror effect has written
        // them), the lines are not re-stamped and the server's lines win: losing
        // the pending row is recoverable, an unresolvable or vanished line is not.
        const stored = readQuoteDraft(id);
        const mintedByOrder = new Map(saved.lines.map((l) => [l.display_order, l.id]));
        const mintedBySentId = new Map<string, string>();
        linesToSave.forEach((d, idx) => {
          const minted = mintedByOrder.get(idx);
          if (isUnsavedLine(d.local_id) && minted) mintedBySentId.set(d.local_id, minted);
        });
        const sentUnsavedIds = new Set(
          linesToSave.filter((d) => isUnsavedLine(d.local_id)).map((d) => d.local_id)
        );
        const remapped = stored?.lines.map((l) => {
          const minted = mintedBySentId.get(l.local_id);
          return minted ? { ...l, local_id: minted } : l;
        });
        const storedIds = new Set(stored?.lines.map((l) => l.local_id));
        const sentAllStored = [...sentUnsavedIds].every((lineId) => storedIds.has(lineId));
        if (
          stored &&
          remapped &&
          sentAllStored &&
          !remapped.some((l) => sentUnsavedIds.has(l.local_id))
        ) {
          // Every identifier the server healed away is dropped from the draft
          // before it is stamped — see healAssembly. Keyed on the line ids
          // remapped above, which is why it runs after that step.
          const savedAssemblyByLine = new Map(
            saved.lines.map((l) => [l.id, configAssembly(l.config)])
          );
          // A line the response does not carry at all — removed in this save, or
          // soft-deleted — is NO INFORMATION, not a heal. Without this, an absent
          // server answer reads as "the boundary rejected everything" and strips a
          // kit the customer still holds (R32).
          const savedLineIds = new Set(saved.lines.map((l) => l.id));
          const savedSnapshot = draftBaseSnapshot(serverSelections(saved, true));
          writeQuoteDraft(id, {
            ...stored,
            base_updated_at: saved.updated_at,
            base_snapshot: savedSnapshot,
            saved_at: Date.now(),
            lines: remapped.map((l) => {
              const local = configAssembly(l.config);
              if (!local || !savedLineIds.has(l.local_id)) return l;
              return {
                ...l,
                config: withAssemblyItemUnitTotal({
                  ...l.config,
                  [ASSEMBLY_ITEM_CONFIG_KEY]: healAssembly(
                    local,
                    savedAssemblyByLine.get(l.local_id)
                  ),
                }),
              };
            }),
          });
          // The mirror effect stamps every later write with this baseline. Moved
          // onto the new version now, not when the refetch lands, so a keystroke
          // between the two is written against the version the overlay will
          // match, and is not discarded as stale along with the draft.
          if (forLoadedQuote()) {
            draftBaselineRef.current = {
              updated_at: saved.updated_at,
              editable: true,
              snapshot: savedSnapshot,
            };
          }
        } else if (stored) {
          // The lines cannot be carried across, so they take the server's copy,
          // exactly as the refetch would. The quote-level answers in the draft —
          // an address picked during the round trip above all — are still the
          // customer's newest and are not the lines' to lose. The baseline stays
          // where it is: a mirror write from state that still holds the
          // synthetic ids must keep reading as stale.
          //
          // Only the lines that cannot be carried take the server's copy. A line the
          // draft already holds under its persisted id keeps the draft's answers: the
          // overlay is about to win with this draft, and a pick made on that line
          // during the round trip (a paper stock, say) would otherwise revert to what
          // this save sent.
          const savedSelections = serverSelections(saved, true);
          const storedById = new Map(stored.lines.map((l) => [l.local_id, l]));
          const savedAssemblyByLine = new Map(
            saved.lines.map((l) => [l.id, configAssembly(l.config)])
          );
          writeQuoteDraft(id, {
            ...stored,
            base_updated_at: saved.updated_at,
            base_snapshot: draftBaseSnapshot(savedSelections),
            saved_at: Date.now(),
            lines: savedSelections.lines.map((d) => {
              const local = storedById.get(d.local_id);
              if (!local) {
                return { local_id: d.local_id, title: d.title, quantity: d.quantity, config: d.config };
              }
              const assembly = configAssembly(local.config);
              if (!assembly) return local;
              return {
                ...local,
                config: withAssemblyItemUnitTotal({
                  ...local.config,
                  [ASSEMBLY_ITEM_CONFIG_KEY]: healAssembly(
                    assembly,
                    savedAssemblyByLine.get(local.local_id)
                  ),
                }),
              };
            }),
          });
        }
      }
      // The draft now stands on this save's version, so a refetch that landed
      // while it was out can be judged.
      settleInFlight();
      // Refresh so the list/snapshot reflects what was just persisted,
      // then confirm to the customer (explicit saves only). For a quote the
      // editor has left, the observers now watch the next quote, so the saved
      // one's cache is marked stale instead and refetched when it is reopened.
      if (routeIdRef.current === id) {
        await quoteQuery.refetch();
        // A rep's save re-imports a sales-controlled order into Nexus, which may
        // re-approve it or clear the approval; the panel above reads that verdict.
        if (salesControlled) void statusQuery.refetch();
      } else {
        void queryClient.invalidateQueries({ queryKey: ['quote', id] });
        void queryClient.invalidateQueries({ queryKey: ['quote-status', id] });
      }
      if (forLoadedQuote()) {
        // Any successful save also clears a failure the status line still shows.
        if (auto) setAutosaveStatus('saved');
        else setAutosaveStatus((s) => (s === 'error' ? 'idle' : s));
      }
      if (!opts?.silent) {
        setSavedNotice(true);
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setSavedNotice(false), 4000);
        // Validate + report through the SAME shared validator the price path
        // uses. Saves stay PERMISSIVE — we persisted regardless and route to
        // sales — but a config can no longer be "green on save" while it would
        // be rejected on price: both run collectConfigMisses. Non-blocking, so
        // the green "Saved" confirmation still shows alongside the to-do list.
        const misses = collectConfigMisses(
          schema,
          linesToSave,
          presentRoles,
          mediaContentQuery.data ?? null,
        );
        if (modeUnanswered) {
          misses.push({
            field_key: DELIVERY_MODE_FIELD_KEY,
            field_label: shipToFieldLabel(DELIVERY_MODE_FIELD_KEY) ?? DELIVERY_MODE_FIELD_KEY,
            line_local_id: '__ship_to__',
            line_index: 0,
            scope: 'ship_to',
          });
        }
        if (misses.length > 0) {
          const errs: Record<string, string> = {};
          for (const m of misses) {
            if (m.field_key === DELIVERY_MODE_FIELD_KEY) {
              errs[m.field_key] = t('edit.error.deliveryModeRequired');
            } else {
              errs[lineFieldKey(m.line_local_id, m.field_key)] = missInlineMessage(m);
            }
          }
          setFieldErrors(errs);
          setFieldSummary({
            source: 'save',
            heading: t('edit.savedNeedDetails', { count: misses.length }),
            trailing: t('edit.autoPriceNote'),
            misses,
          });
        } else {
          // Complete + valid — clear any issue report left from a prior click.
          setFieldErrors({});
          setFieldSummary(null);
        }
      }
      if (forLoadedQuote()) lastSaveFailedRef.current = false;
      return true;
    } catch (err) {
      if (forLoadedQuote()) lastSaveFailedRef.current = true;
      if (auto) {
        // Quiet: the edits stay in the local draft, and the status line says so.
        if (forLoadedQuote()) {
          failedSnapshotRef.current = sentSnapshot;
          setAutosaveStatus('error');
        }
        return false;
      }
      const envelope = extractApiError(err);
      const message = envelope?.message ?? t('edit.error.saveFailed');
      // If the server pinned the failure to specific fields, surface
      // them inline + in the near-button list — same UX as the pricing
      // pre-flight. Otherwise drop to the generic near-button banner.
      const fieldKeys = readFieldKeys(envelope?.details);
      if (fieldKeys && fieldKeys.length > 0) {
        applyFieldKeyFailure({
          source: 'save',
          heading: t('edit.error.saveFields', { count: fieldKeys.length }),
          fieldKeys,
          message,
          details: envelope?.details,
          sentLineIds: linesToSave.map((d) => d.local_id),
        });
      } else {
        setBannerError(message);
      }
      return false;
    } finally {
      settleInFlight();
      if (!auto) setSaving(false);
    }
  }

  async function handleGetPricing(): Promise<void> {
    // Captured BEFORE any state resets below: true when the button the
    // customer just clicked read "Submit for quote" (the prior attempt
    // already came back CANNOT_PRICE). Threaded through to
    // applyPricingResponse so a repeat CANNOT_PRICE renders the "submitted"
    // confirmation instead of the generic contact-sales banner again.
    const isResubmitForQuote =
      pricingState.kind === 'priced' &&
      !pricingState.response.ok &&
      pricingState.response.failure.code === 'CANNOT_PRICE';
    setValidationRevealSeq((n) => n + 1);
    setBannerError(null);
    setFieldErrors({});
    setFieldSummary(null);
    setCustomQuoteConfirmed(false);

    // Client-side pre-flight — catch the obvious "you didn't fill a
    // required field" case before we round-trip to the ERP. Cheaper +
    // gives a direct, scrolled-to-field UX instead of a generic banner.
    //
    // Per the agreed UX: render failures in exactly TWO places —
    // (1) inline red text on each missing field (handled by SchemaForm
    //     via the `errors` prop), and
    // (2) a summary banner next to the action buttons listing the
    //     missing field labels as click-to-scroll links.
    const misses = collectConfigMisses(
      schema,
      drafts!,
      presentRoles,
      mediaContentQuery.data ?? null,
    );
    if (misses.length > 0) {
      const errs: Record<string, string> = {};
      // Key by the line-namespaced id so the inline error lights up only the
      // project that's actually missing the field (not every project's copy).
      for (const m of misses) errs[lineFieldKey(m.line_local_id, m.field_key)] = missInlineMessage(m);
      setFieldErrors(errs);
      setFieldSummary({
        source: 'preflight',
        heading: t('edit.needDetails', { count: misses.length }),
        trailing: t('edit.saveAnytimeNote'),
        misses,
      });
      // Don't paint the bottom PricingBlock — the near-button banner +
      // inline field errors are the right UX here. Reset pricing state
      // so any prior price stays visible only if it's already valid.
      setPricingState({ kind: 'idle' });
      // Save what we have — saves are intentionally permissive. Quotes
      // missing required fields can still be saved and routed to sales
      // for review; pricing just needs them filled before it can run.
      void handleSave({ silent: true });
      // Reveal the first missing field — jumps the relevant project's wizard
      // to that field's step, then scrolls/focuses it. Direct repair beats a
      // generic banner the customer has to chase.
      revealMiss(misses[0]!);
      return;
    }

    // Save first so the pricing call works against the current draft. A refused
    // save stops here, its reason already shown beside the field: pricing reads
    // the order as last STORED, so pricing past a refusal would quote — and "Submit
    // for quote" would confirm — a version the customer has since changed, while
    // Save says the same answers cannot be kept.
    if (!(await handleSave({ silent: true }))) {
      setPricingState({ kind: 'idle' });
      return;
    }

    // No pricability decision here — specialty finishing, order minimums, and any
    // other "can't auto-price this" ruling belong to Nexus (ADR-013). We always
    // call /price and render whatever Nexus returns: a price, or a
    // CANNOT_PRICE/INVALID_CONFIG outcome (with a Nexus-set reason_code and any
    // Nexus-owned number) that PricingBlock → customerFacingFailure renders in the
    // portal's own voice.
    setPricingState({ kind: 'pricing' });
    try {
      const response = await quotesApi.priceQuote(id!);
      // Both outcomes, separated by `ok` — a price that came back CANNOT_PRICE
      // or INVALID_CONFIG is the interesting half, and dropping it would leave
      // the funnel unable to tell "did not try" from "tried and was refused".
      // `failure_code` is Nexus's closed enum; no message text and no numbers.
      trackEvent(ANALYTICS_EVENTS.quote_priced, {
        ok: response.ok,
        failure_code: response.ok ? null : response.failure.code,
      });
      applyPricingResponse(response, { isResubmitForQuote });
      if (response.ok) {
        // Refresh the quote to pick up the new snapshot.
        await quoteQuery.refetch();
      }
    } catch (err) {
      // The portal API returns 4xx for INVALID_CONFIG / CANNOT_PRICE etc.
      // with body { ok: false, failure: {...} } — axios throws on 4xx, but
      // the failure envelope is still the right shape for PricingBlock.
      // Hoist it back into the priced-state path so the customer sees the
      // structured failure (field_keys, contact-sales link) instead of a
      // generic banner.
      const failure = extractPricingFailure(err);
      // Optional hardening: if Nexus took this quote over between page load
      // and this click, the pricing call comes back SALES_CONTROLLED. Don't
      // show a generic pricing error — refetch status so the surface
      // re-renders locked + read-only (the rep now owns the line set).
      if (failure && (failure.code as string) === 'SALES_CONTROLLED') {
        void statusQuery.refetch();
        setPricingState({ kind: 'idle' });
        return;
      }
      if (failure) {
        applyPricingResponse({ ok: false, failure }, { isResubmitForQuote });
        return;
      }
      // Network or other transport-level failure — the portal-backend
      // itself didn't respond, so we don't have a structured failure
      // DTO. Use the same copy helper PricingBlock uses (with
      // `failure = null`) so the customer sees the same voice and the
      // same path forward (email sales) instead of a generic toast.
      const copy = customerFacingFailure(null);
      setBannerError(`${copy.headline} ${copy.body}`);
      setPricingState({ kind: 'idle' });
    }
  }

  function applyPricingResponse(
    response: PricingResponseDto,
    opts?: { isResubmitForQuote?: boolean },
  ): void {
    setPricingState({ kind: 'priced', response });
    // A CANNOT_PRICE that arrives from an explicit "Submit for quote" click
    // (not the first "See my price" attempt) confirms the info went in —
    // show the "we've got it" banner instead of repeating the generic
    // contact-sales copy verbatim.
    setCustomQuoteConfirmed(
      !response.ok && response.failure.code === 'CANNOT_PRICE' && Boolean(opts?.isResubmitForQuote),
    );
    if (!response.ok) {
      const fks = response.failure.details?.field_keys;
      if (Array.isArray(fks) && fks.length > 0) {
        applyFieldKeyFailure({
          source: 'pricing',
          // Both branches read identically today — kept as a single key rather
          // than a spurious plural pair (see rule: only pluralize a genuine
          // singular/plural pair).
          heading: t('edit.error.priceReview'),
          fieldKeys: fks,
          message: response.failure.message,
        });
      }
    }
  }

  // Extract `details.field_keys` from an API envelope. Returns null when
  // the field isn't present or isn't an array of strings.
  function readFieldKeys(details: Record<string, unknown> | undefined): string[] | null {
    if (!details) return null;
    const fks = (details as { field_keys?: unknown }).field_keys;
    if (!Array.isArray(fks)) return null;
    const out = fks.filter((k): k is string => typeof k === 'string');
    return out.length > 0 ? out : null;
  }

  // Which assembly item a kit miss belongs to, by page index (-1 when the order
  // holds none). The wire carries no line attribution with an `assembly.*` or
  // packing-axis key (ADR-019), so the owner is found by asking each item the
  // question the pre-flight asks: the first assembly item whose own kit reports
  // this key is the one the customer has to fix. Failing that — a key only the
  // server or the ERP can judge — the first assembly item is the best anchor
  // the page has.
  //
  // `packingAxis` picks the grain of the judge. A hand-written kit control is
  // reported with the rest of the item's line config (lineConfigIssues, exactly
  // as collectConfigMisses runs it); a packing axis is a quote-scope schema
  // field bound to the item's snapshot, so it is judged at that scope over that
  // snapshot, as its SchemaForm renders it.
  function assemblyItemIndexFor(anchorKey: string, packingAxis: boolean): number {
    const list = drafts ?? [];
    const reports = (d: LineDraft): boolean =>
      (packingAxis
        ? lineConfigIssues(schema, (configAssembly(d.config) ?? {}) as FieldValues, {
            scope: 'quote',
          })
        : lineConfigIssues(schema, d.config)
      ).some((issue) => issue.field_key === anchorKey);
    const reporting = list.findIndex((d) => isAssemblyLine(d) && reports(d));
    return reporting >= 0 ? reporting : list.findIndex(isAssemblyLine);
  }

  // Route a field-keyed failure (from save/submit/pricing) through the
  // same surface as the client-side pre-flight: inline red text on each
  // field + the near-button summary with click-to-scroll labels.
  function applyFieldKeyFailure(args: {
    source: 'pricing' | 'save' | 'submit';
    heading: string;
    fieldKeys: string[];
    message: string;
    trailing?: string;
    // The failure envelope's `details`: a sentence per key, when the server
    // wrote one (the portal's own save boundary does), used in place of the
    // one shared `message` so each field says what is wrong with IT.
    details?: Record<string, unknown>;
    // The local ids of the `lines` the failed save SENT, in the order sent —
    // what a `lines.<n>.<key>` failure key indexes into.
    sentLineIds?: readonly string[];
  }): void {
    const errs: Record<string, string> = {};
    const misses: PreflightMiss[] = [];
    const messageFor = (k: string): string => {
      const own = args.details?.[k];
      return typeof own === 'string' && own.trim() !== '' ? own : args.message;
    };
    for (const k of args.fieldKeys) {
      // One item's field, attributed by the save that sent it. Anchored to THAT
      // item, never item 1. A key the page renders no control for (a structural
      // column like display_order) is still named, with the server's sentence,
      // because that sentence already says which item.
      const lineIssue = parseLineIssueKey(k);
      if (lineIssue) {
        const localId = args.sentLineIds?.[lineIssue.line_index];
        const lineIndex = localId ? (drafts ?? []).findIndex((d) => d.local_id === localId) : -1;
        const anchorKey = lineIssueAnchorKey(lineIssue.field_key);
        const fld = schema.fields.find((f) => f.key === anchorKey);
        const isTitle = anchorKey === LINE_TITLE_FIELD_KEY;
        const isKit = anchorKey.startsWith(`${ASSEMBLY_FIELD_PREFIX}.`);
        const label = isTitle
          ? LINE_TITLE_FIELD_LABEL
          : (fld?.label ?? ORDER_SERVICE_FIELD_LABELS[anchorKey] ?? fieldKeyLabel(anchorKey));
        if (localId && lineIndex >= 0 && (fld || isTitle || isKit)) {
          errs[lineFieldKey(localId, anchorKey)] = messageFor(k);
          misses.push({
            field_key: anchorKey,
            field_label: label,
            line_local_id: localId,
            line_index: lineIndex,
          });
        } else {
          errs[k] = messageFor(k);
          misses.push({
            field_key: k,
            field_label: label,
            line_local_id: '__unanchored__',
            line_index: 0,
            scope: 'unanchored',
            detail: messageFor(k),
          });
        }
        continue;
      }
      // Ship-to keys (step 12) aren't in the per-line schema — resolve their
      // label from the shared map and mark the miss as quote-level so the
      // summary skips the "(project N)" suffix and reveal just scrolls to it.
      // Quote-level surfaces render their input ids as the bare field key, so
      // the error key stays bare too.
      // Resolves BOTH key shapes: bare `ship_to.<prop>` for address one and
      // `ship_to.<n>.<prop>` for the ones after it (whose label names the address,
      // so the summary says which). An indexed key looked up in the flat label map
      // would resolve to nothing and the miss would vanish from the summary.
      const shipLabel = shipToFieldLabel(k);
      if (shipLabel) {
        errs[k] = messageFor(k);
        misses.push({
          field_key: k,
          field_label: shipLabel,
          line_local_id: '__ship_to__',
          line_index: 0,
          scope: 'ship_to',
        });
        continue;
      }
      // An assembly item's kit: its hand-written controls (`assembly.*`) and the
      // packing-detail axes (a quote-SCOPE schema field — the grain names the
      // snapshot a field binds to, not a page singleton). Both render inside an
      // `assembly` item's own panel with line-namespaced ids (fieldDomId), so
      // the miss anchors to that item like any per-line field. The one assembly
      // flow is the item (ADR-020); no quote-level mount is left to anchor to.
      //
      // An axis key may arrive bare or `assembly.`-prefixed; either way the
      // anchor is the bare axis key, because that is the id the axis's
      // SchemaForm renders.
      const isKitKey = k.startsWith(`${ASSEMBLY_FIELD_PREFIX}.`);
      const axisKey = isKitKey ? k.slice(ASSEMBLY_FIELD_PREFIX.length + 1) : k;
      const axisField = schema.fields.find(
        (f) => f.key === axisKey && fieldScope(f, schemaGroupScope) === 'quote'
      );
      if (isKitKey || axisField) {
        const anchorKey = axisField ? axisField.key : k;
        const itemIndex = assemblyItemIndexFor(anchorKey, axisField !== undefined);
        const assemblyItem = itemIndex >= 0 ? drafts?.[itemIndex] : undefined;
        const label = ORDER_SERVICE_FIELD_LABELS[k] ?? axisField?.label ?? fieldKeyLabel(k);
        if (assemblyItem) {
          errs[lineFieldKey(assemblyItem.local_id, anchorKey)] = messageFor(k);
          misses.push({
            field_key: anchorKey,
            field_label: label,
            line_local_id: assemblyItem.local_id,
            line_index: itemIndex,
          });
          continue;
        }
        // No assembly item on the page, so no control renders this key. Named in
        // the summary rather than dropped (FB-0051), with nothing to scroll to.
        errs[k] = messageFor(k);
        misses.push({
          field_key: k,
          field_label: label,
          line_local_id: '__unanchored__',
          line_index: 0,
          scope: 'unanchored',
          ...(typeof args.details?.[k] === 'string' ? { detail: messageFor(k) } : {}),
        });
        continue;
      }
      // One item's fulfillment answer. The key names the item by its line id, so
      // the summary names the item; the control renders in the fulfillment
      // section under the bare key, which is what the reveal scrolls to.
      const targetItem = fulfillmentTargetItemFor(k);
      if (targetItem) {
        errs[k] = messageFor(k);
        misses.push({
          field_key: k,
          field_label: t('orderServices.fulfillment.targets.missLabel', {
            item: targetItem.label,
          }),
          line_local_id: '__order_services__',
          line_index: 0,
          scope: 'order_services',
        });
        continue;
      }
      // Order-services keys (step 14: fulfillment.* and the shipping speed) are
      // quote-level — resolve from the shared map, same quote-level scope.
      const serviceLabel = ORDER_SERVICE_FIELD_LABELS[k];
      if (serviceLabel) {
        errs[k] = messageFor(k);
        misses.push({
          field_key: k,
          field_label: serviceLabel,
          line_local_id: '__order_services__',
          line_index: 0,
          scope: 'order_services',
        });
        continue;
      }
      // The item NAME, which is a QuoteLine column with no schema field behind it,
      // so the lookup below would miss it and drop it into the unanchored branch.
      // The wire carries no project attribution, so anchor it to the first item
      // exactly as a bare per-line schema key is anchored.
      if (k === LINE_TITLE_FIELD_KEY) {
        const lineLocalId = drafts?.[0]?.local_id ?? '';
        errs[lineFieldKey(lineLocalId, k)] = messageFor(k);
        misses.push({
          field_key: k,
          field_label: LINE_TITLE_FIELD_LABEL,
          line_local_id: lineLocalId,
          line_index: 0,
        });
        continue;
      }
      // Per-line schema field. The ERP returns a bare field key with no project
      // attribution, so we anchor it to the first project (as before) and
      // namespace the error key to that line so SchemaForm finds it.
      const fld = schema.fields.find((f) => f.key === k);
      if (fld) {
        const lineLocalId = drafts?.[0]?.local_id ?? '';
        errs[lineFieldKey(lineLocalId, k)] = messageFor(k);
        misses.push({
          field_key: k,
          field_label: fld.label,
          line_local_id: lineLocalId,
          line_index: 0,
        });
      } else {
        // Unknown key (neither quote-level nor a current schema field). Keep the
        // bare error entry AND name it in the summary via the shared label
        // resolver — dropping it was how a blocked action could come back with
        // nothing on screen at all (FB-0051: "the order wouldn't go through but
        // didn't tell me why"). Marked 'unanchored': no control renders it, so
        // the summary states it rather than offering a scroll that goes nowhere.
        errs[k] = messageFor(k);
        misses.push({
          field_key: k,
          field_label: fieldKeyLabel(k),
          line_local_id: '__unanchored__',
          line_index: 0,
          scope: 'unanchored',
          ...(typeof args.details?.[k] === 'string' ? { detail: messageFor(k) } : {}),
        });
      }
    }
    setFieldErrors(errs);
    if (misses.length > 0) {
      setFieldSummary({
        source: args.source,
        heading: args.heading,
        trailing: args.trailing,
        misses,
      });
      // Scroll to the first miss the page can actually show. An unanchored miss
      // has no DOM node, so leading with it would scroll nowhere and leave the
      // customer where they were.
      revealMiss(misses.find((m) => m.scope !== 'unanchored') ?? misses[0]!);
    } else {
      // Defensive: never return from a blocked action silently. Every branch
      // above pushes a miss today, so this only fires if that ever stops being
      // true — and then the customer still reads why.
      setBannerError(args.message);
    }
  }

  function toggleLineCollapsed(local_id: string): void {
    setCollapsedLines((cur) => {
      const next = new Set(cur);
      if (next.has(local_id)) next.delete(local_id);
      else next.add(local_id);
      return next;
    });
  }

  function collapseLine(local_id: string): void {
    setCollapsedLines((cur) => {
      if (cur.has(local_id)) return cur;
      const next = new Set(cur);
      next.add(local_id);
      return next;
    });
  }

  function expandLine(local_id: string): void {
    setCollapsedLines((cur) => {
      if (!cur.has(local_id)) return cur;
      const next = new Set(cur);
      next.delete(local_id);
      return next;
    });
  }

  function scrollToField(domId: string): void {
    // Try a couple of frames out so React has rendered any newly
    // surfaced error state (which can shift layout).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(domId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Programmatic focus to make screen-reader handoff clean.
          if (typeof (el as HTMLElement).focus === 'function') {
            (el as HTMLElement).focus({ preventScroll: true });
          }
        }
      });
    });
  }

  // Reveal a flagged field: scroll/focus it. In the dynamic single-form layout
  // every applicable field is already on screen — a missing required field is
  // by definition visible (preflight only flags fields whose conditional_show
  // passes), and the quote-level ship_to.* inputs render with the editor — so
  // there's no step to navigate to. We scroll straight to the input. Per-line
  // schema fields carry a line-namespaced DOM id (missDomId), so the scroll
  // lands on the right project's copy; quote-level inputs use the bare key.
  function revealMiss(miss: PreflightMiss): void {
    // A per-line field inside a collapsed item is not in the layout, so the
    // scroll would land nowhere. Open the item first; scrollToField already
    // waits two frames for the resulting render.
    if (!miss.scope) expandLine(miss.line_local_id);
    scrollToField(missDomId(miss));
  }

  // Jump from a review-row card back to the item it summarizes. Same two moves
  // revealMiss makes for a flagged field: open the item if it's collapsed (a
  // collapsed card's configurator isn't in the layout), then bring it into view
  // — and hand FOCUS to the card, not just the viewport, so activating "Edit
  // item 3" from the keyboard lands the caret on item 3 instead of leaving it on
  // a button now scrolled off screen. The card carries tabIndex={-1} for exactly
  // this (it is not in the tab order). Two frames out, like scrollToField, so the
  // expand above has rendered before we look the element up.
  function revealLine(local_id: string, index: number): void {
    expandLine(local_id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`project-line-${index}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.focus({ preventScroll: true });
      });
    });
  }

  // `config` is the appended project's starting spec — `{}` for the plain Add
  // action (the product picker shows and the customer chooses), or a seed when
  // something else already knows what the project is: an entry door's part on a
  // re-entry, or a composition tile that owns this item.
  //
  // `afterLocalId` is the item whose own footer control was used — the new item
  // lands directly beneath it, so "add" from the middle of a long order doesn't
  // fling the customer to the bottom of the page. The actions-row control passes
  // nothing and appends, as before.
  async function addLine(config: FieldValues = {}, afterLocalId?: string): Promise<void> {
    const cur = drafts ?? [];
    const afterIndex = afterLocalId ? cur.findIndex((d) => d.local_id === afterLocalId) : -1;
    // Position of the about-to-be-inserted project. `index` is stable across the
    // refetch-driven remount below (local_id changes new-<ts> → real id, but the
    // project's position does not), so it's the reliable scroll anchor.
    const newIndex = afterIndex >= 0 ? afterIndex + 1 : cur.length;
    const nextDrafts: LineDraft[] = [
      ...cur.slice(0, newIndex),
      {
        local_id: `new-${Date.now()}`,
        title: '',
        quantity: 100,
        config,
        unit_price: null,
        line_price: null,
      },
      ...cur.slice(newIndex),
    ];
    setDrafts(nextDrafts);
    // Adding from an item's own footer says that item is done, so fold it away
    // and the new one opens against a short page rather than the foot of a long
    // one. View state only — the item stays mounted, validated and transmitted
    // exactly as it was, and its header reopens it. Skipped for the actions-row
    // control (nothing to fold) and for a source line with no persisted id yet,
    // since collapsedLines is keyed on local_id and the save below remaps a
    // new-<ts> id to the real one.
    if (afterLocalId && !isUnsavedLine(afterLocalId)) collapseLine(afterLocalId);
    // Auto-persist the appended line so it gets a real DB id immediately —
    // artwork attaches to a QuoteLine by id, and isUnsavedLine() gates the
    // uploader on that. Without this, a just-added line shows the
    // "save first" placeholder until the next manual Save. Silent so the
    // "Saved" toast doesn't fire on a plain Add. refetch() inside handleSave
    // re-maps every line (incl. the new one) with its persisted id, flipping
    // isUnsavedLine() to false so the ArtworkUploader renders.
    await handleSave({ silent: true, lines: nextDrafts });
    // Bring the new project into view — it's otherwise off-screen below the
    // fold — and hand FOCUS to it, the same way revealLine does: the page has
    // just moved under the customer (the source item folded away above), so
    // leaving the caret on the footer button they pressed strands a keyboard or
    // screen-reader user somewhere the viewport no longer is. The card carries
    // tabIndex={-1} for exactly this. rAF waits for the post-save render to
    // paint the element before we look it up.
    requestAnimationFrame(() => {
      const el = document.getElementById(`project-line-${newIndex}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    });
  }

  function removeLine(local_id: string): void {
    const cur = drafts ?? [];
    const index = cur.findIndex((d) => d.local_id === local_id);
    setDrafts((prev) => (prev ?? []).filter((d) => d.local_id !== local_id));
    expandLine(local_id);
    // The Remove control unmounts with its item and focus falls to <body>, so
    // land it on the item now standing in that place (or the new last one), the
    // same card the add and copy paths focus.
    requestAnimationFrame(() => {
      document
        .getElementById(`project-line-${Math.min(index, cur.length - 2)}`)
        ?.focus({ preventScroll: true });
    });
  }

  // Clone an existing project into a new one, inserted right after it. Copies the
  // whole spec (config blob), quantity, and title — the fast path for "another
  // piece just like this one, tweak a detail". Artwork does NOT carry over:
  // uploads live in Asset rows keyed to the source line's id (not in config), so
  // the copy starts with empty upload slots the customer fills for the new piece.
  // Auto-persists (like addLine) so the copy gets a real DB id and its uploader
  // renders immediately, then scrolls it into view.
  async function duplicateLine(local_id: string): Promise<void> {
    const cur = drafts ?? [];
    const srcIndex = cur.findIndex((d) => d.local_id === local_id);
    const src = cur[srcIndex];
    if (!src) return;
    // The door marker is not copied: it asserts "the entry door chose this
    // product type on this line", which is false for a copy the customer made,
    // and it is never cleared — carrying it would leave the copy with no
    // product-type picker for the rest of its life. Stripped, the copy renders
    // the ordinary grid with the source's product already selected, exactly like
    // a line the customer picked themselves.
    const {
      [DOOR_PRESELECTED_PRODUCT_TYPE_KEY]: _doorMarker,
      ...copiedConfig
    } = structuredClone(src.config);
    const copy: LineDraft = {
      local_id: `new-${Date.now()}`,
      title: src.title ? `${src.title} (copy)` : '',
      quantity: src.quantity,
      // Deep-copied above so editing the copy's nested config (multi-selects,
      // sub-field objects) never mutates the source project's blob.
      config: lineConfigWithFreshAssemblyIds(copiedConfig, newKitContentRowId),
      unit_price: null,
      line_price: null,
    };
    const newIndex = srcIndex + 1;
    const nextDrafts: LineDraft[] = [
      ...cur.slice(0, newIndex),
      copy,
      ...cur.slice(newIndex),
    ];
    setDrafts(nextDrafts);
    await handleSave({ silent: true, lines: nextDrafts });
    requestAnimationFrame(() => {
      document
        .getElementById(`project-line-${newIndex}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function updateLine(local_id: string, patch: Partial<LineDraft>): void {
    setDrafts((cur) =>
      (cur ?? []).map((d) => (d.local_id === local_id ? { ...d, ...patch } : d))
    );
  }

  // Only saved items carry a chevron to reopen them, so only they are folded.
  const foldableLineIds = drafts.filter((d) => !isUnsavedLine(d.local_id)).map((d) => d.local_id);
  const allFolded = foldableLineIds.every((lineId) => collapsedLines.has(lineId));
  const foldAllControl =
    foldableLineIds.length > 1 ? (
      <button
        type="button"
        className="link-button fold-all-toggle"
        aria-expanded={!allFolded}
        onClick={() =>
          setCollapsedLines(allFolded ? new Set<string>() : new Set(foldableLineIds))
        }
      >
        {allFolded ? t('edit.collapse.expandAll') : t('edit.collapse.collapseAll')}
      </button>
    ) : undefined;

  return (
    <div ref={editorRootRef}>
      <div className="card">
        <EntityNameHeader
          eyebrow={t('edit.tab.order')}
          level="h2"
          name={title || t('edit.untitledOrder')}
          badges={
            <>
              <span className="tag" style={{ marginLeft: 8 }}>
                {quoteStatusLabel(quote.status)}
              </span>
              {nexusQuoteNumber && (
                <span className="tag tag--linked" style={{ marginLeft: 8 }}>
                  {t('status:approval.quoteNumberLabel', { number: nexusQuoteNumber })}
                </span>
              )}
              {quote.is_historical && (
                <span className="tag tag--historical" style={{ marginLeft: 8 }}>
                  {t('edit.badge.pastOrder')}
                </span>
              )}
              {quote.is_partial && (
                <span className="tag tag--partial" style={{ marginLeft: 8 }}>
                  {t('edit.badge.needsReview')}
                </span>
              )}
            </>
          }
          trailing={
            <>
              {foldAllControl}
              <Link to="/quotes">{t('edit.backToList')}</Link>
            </>
          }
        />
        {/* Past-order context + review. Never renders internal import keys: the
            "what to review" list is derived live from the shared field validators
            (importReview → human field labels + click-to-scroll), so a partner
            sees "How many do you need?", not `line_<uuid>.quantity`. Amber when
            something needs attention, quiet blue when it's just context. */}
        {(quote.is_historical || quote.is_partial) && (
          <div
            className={`banner ${importReview.length > 0 ? 'banner--warn' : 'banner--info'}`}
            role={importReview.length > 0 ? 'alert' : 'status'}
            aria-live="polite"
            style={{ marginTop: 16 }}
          >
            <strong>{t('edit.pastOrderNote')}</strong> {t('edit.pastOrderBody')}
            {quote.legacy_quote_id && (
              <p style={{ margin: '8px 0 0' }}>
                <Trans
                  i18nKey="edit.originalQuoteNumber"
                  t={t}
                  values={{ id: quote.legacy_quote_id }}
                  components={[<strong key="id" />]}
                />
              </p>
            )}
            {importReview.length > 0 && (
              <>
                <p style={{ margin: '12px 0 4px' }}>
                  {productTypeMissingCount > 0
                    ? t('edit.import.mismatchIntro', {
                        scope: t(
                          drafts && productTypeMissingCount >= drafts.length
                            ? 'edit.import.scopeWhole'
                            : 'edit.import.scopePart',
                        ),
                      })
                    : t('edit.import.minorMismatchIntro')}
                </p>
                <ul style={{ margin: '4px 0 0', paddingLeft: '1.25rem' }}>
                  {importReviewGroups.map((g) => {
                    const multiProject = !g.scope && !!drafts && drafts.length > 1;
                    // One miss → the familiar single link. Several projects missing
                    // the SAME field → one line with compact numbered jump-links, so
                    // "product type on 7 projects" is one row, not seven.
                    if (g.misses.length === 1) {
                      const m = g.misses[0]!;
                      return (
                        <li key={g.key}>
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => revealMiss(m)}
                          >
                            {g.field_label}
                            {multiProject ? t('edit.projectSuffix', { n: m.line_index + 1 }) : ''}
                          </button>
                        </li>
                      );
                    }
                    return (
                      <li key={g.key}>
                        {g.field_label} {t('edit.import.groupCount', { count: g.misses.length })}{' '}
                        {g.misses.map((m, i) => (
                          <span key={`${m.line_local_id}-${m.field_key}`}>
                            {i > 0 ? ' ' : ''}
                            <button
                              type="button"
                              className="link-button"
                              aria-label={t('edit.import.jumpToProjectAria', {
                                label: g.field_label,
                                n: m.line_index + 1,
                              })}
                              onClick={() => revealMiss(m)}
                            >
                              {m.line_index + 1}
                            </button>
                          </span>
                        ))}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        )}
        {/* The Order heading above IS the label — it mirrors this field live as
            the customer types, so the field carries only an accessible name +
            the inline hint, not a redundant visible <label>. */}
        <div className="form-row" style={{ marginTop: 16 }}>
          <input
            id="project_title"
            type="text"
            aria-label={t('edit.orderNameLabel')}
            placeholder={t('edit.orderNamePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            // A text answer keeps its value in the DOM, so past editing it only
            // needs `readOnly` — a disabled input leaves the tab order and takes
            // the order's name with it. `input--locked` is what stops it looking
            // editable. Unlike the per-project name below, no heading mirrors this
            // one, so the field IS the read-back and cannot simply drop.
            {...(isEditable
              ? {}
              : { readOnly: true, 'aria-readonly': true, className: 'input--locked' })}
          />
          <span className="help">{t('edit.orderNameHint')}</span>
        </div>
      </div>

      {/* Nexus verdict surface — locked/approved/pending/declined views.
          Returns null for an ordinary self-service quote (control='portal',
          approval none), leaving the configurator below untouched. */}
      {quoteStatus && (
        <QuoteApprovalPanel
          status={quoteStatus}
          quoteNumber={nexusQuoteNumber}
          paySupported={!impersonating}
          onPay={() => navigate(`/quotes/${id}/checkout`)}
          // Hide the settlement button whenever the quote has already advanced
          // past approved-unpaid (paid, or pending human review) — Nexus may
          // still report 'approved', so the local status is what tells us the
          // partner already settled. An open change order holds payment too:
          // the order is being re-priced and re-signed in Nexus.
          actionsDisabled={
            quote.status === 'pending_approval' ||
            quote.status === 'paid' ||
            Boolean(quoteStatus.change_order)
          }
          submitted={quote.status !== 'draft' && quote.status !== 'priced'}
        />
      )}

      {/* The quote-level Need-By card, standalone, for whenever the first
          item's configurator cannot host it (see needByAnchorDetached). A
          collapsed FIRST item is the common route: the anchored card is inside
          the hidden body and cannot be reached. The detached card sits ABOVE
          the items rather than below them so collapsing item 1 moves the card
          as little as possible — anchored it sits near the top of item 1, which
          is a few hundred pixels away, not the foot of a multi-item order. */}
      {needByAnchorDetached && (
        <NeedByPicker
          today={todayIso}
          turnaroundDays={quoteTurnaroundDays}
          value={finishDate}
          onChange={setFinishDate}
          expedited={expediteRequested}
          onExpeditedChange={setExpediteRequested}
          itemCount={drafts.length}
          disabled={!isEditable}
        />
      )}

      {/* Spec editor. Always rendered, so every order shows the whole form as
          it was entered; isEditable alone decides whether it takes edits. */}
      {drafts.map((d, idx) => (
        <LineEditor
          key={d.local_id}
          schema={schema}
          draft={d}
          index={idx}
          quoteId={id}
          isEditable={isEditable}
          impersonating={impersonating}
          isHistorical={quote.is_historical}
          quoteTitle={title}
          fieldErrors={fieldErrors}
          canRemove={canRemoveLine}
          onPatch={(patch) => updateLine(d.local_id, patch)}
          // An assembly item's kit may contain the order's OTHER items, never
          // itself: a kit cannot be one of its own components.
          kitLines={referenceableLines.filter((o) => o.id !== d.local_id)}
          hasUnsavedLines={hasUnsavedLines}
          onRequestSave={requestSave}
          onPersistMediaUploadChoice={() => persistMediaUploadChoice(d.local_id)}
          ruleErrorRevealSeq={validationRevealSeq}
          onRemove={() => removeLine(d.local_id)}
          collapsed={collapsedLines.has(d.local_id)}
          onToggleCollapsed={() => toggleLineCollapsed(d.local_id)}
          // The same control the actions row carries, repeated at the foot of
          // every item so "add another" is reachable where the customer
          // finishes one — and inserting right below THIS item, copying THIS
          // item, rather than always acting on the last one.
          footer={
            isEditable ? (
              <AddProjectActions
                copyScope="this"
                disabled={saving}
                copyTargetLabel={
                  d.title || deriveProjectName(schema, d.config) || t('shared.untitledProject')
                }
                onAdd={() => void addLine({}, d.local_id)}
                onCopyTarget={() => void duplicateLine(d.local_id)}
              />
            ) : undefined
          }
          onAssetsChanged={() => void assetsQuery.refetch()}
          // The door this quote was created through. The only thing it can
          // change is which product cards the picker OFFERS, and no option
          // narrows itself today — every door offers every family, because one
          // job may combine print, media and assembly work and reaches Nexus
          // as a single contract (ADR-018 Decision 8). A stored value is never
          // refused or hidden by it either way.
          entryDivisionKey={quote.entry_division_key}
          // Anchor the quote's single Need-By card to the first project's
          // configurator, after the Proofs section. Quote-level singleton, so
          // it is restricted to one line.
          renderAfterGroup={(groupKey) =>
            idx === 0 && !collapsedLines.has(d.local_id)
              ? renderNeedByAfterGroup(groupKey)
              : null
          }
          // A barcode is required when Argo will warehouse + fulfill the order
          // (quote-level service); the artwork advisory adjusts its copy live as
          // the customer toggles Fulfillment below.
          barcodeRequired={orderRequiresBarcode(orderServices)}
        />
      ))}

      {/* Step 14 — the quote-level "what do you need us to do with it?" section
          (shipping / pickup / fulfillment — the mutually-exclusive delivery
          modes). Assembly is not one of them: kitting is an `assembly` item in
          the list above (ADR-020). Shipping is gated inside it: the Shipping card reveals the
          destination (ShipToForm), and the card the customer clicks is the
          stored delivery mode — none lit until they pick.
          Completion of the selected services is enforced at submit by the
          combined pre-block + the authoritative backend gates
          (assertShipToReady + assertOrderServicesReady). No service is priced
          here. Rendered read-only past editing, so the order's delivery answers
          stay on screen as entered. */}
      <OrderServicesForm
        value={orderServices}
        onChange={setOrderServices}
        shipTos={shipTos}
        onShipTosChange={setShipTos}
        orderQuantity={splitOrderQuantity}
        physicalProofSelected={physicalProofSelected}
        fulfillmentItems={fulfillmentItems}
        onPatchItemFulfillmentTarget={patchLineFulfillmentTarget}
        proofQuantity={proofQuantity}
        onProofQuantityChange={setProofQuantity}
        // One blank sample address whenever nothing is captured yet, so the
        // block opens with a row to fill rather than with only an Add button.
        // A display default, not stored state: until the customer types, the
        // quote genuinely has no sample destination and the save sends none.
        proofShipTos={
          isEditable && proofShipTos.length === 0 ? SEEDED_PROOF_SHIP_TOS : proofShipTos
        }
        onProofShipTosChange={setProofShipTos}
        errors={fieldErrors}
        disabled={!isEditable}
        headerAction={foldAllControl}
      />

      {/* Pre-submit review of the whole order — every item's spec side by side,
          the last thing above the action buttons. Renders itself only for a
          multi-item order (a single item already reads back in its own rail). */}
      <ProjectSummaryStrip
        schema={schema}
        items={summaryStripItems}
        quoteTitle={title}
        isEditable={isEditable}
        onEdit={revealLine}
      />

      {isEditable && (
        <div className="card">
          {fieldSummary && fieldSummary.misses.length > 0 ? (
            // Near-button summary of problem fields — covers preflight
            // (missing required), pricing rejection (ERP can't price
            // these), save rejection, and submit rejection. Mirrors the
            // inline red text on each field so the customer sees the
            // same message in two places (Nielsen Norman §Inline
            // Validation). Each field name is click-to-scroll for
            // direct repair.
            <div
              id="action-feedback"
              className="banner banner--error"
              role="alert"
              aria-live="polite"
              style={{ marginBottom: 16 }}
            >
              <strong>{fieldSummary.heading}</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: '1.25rem' }}>
                {fieldSummary.misses.map((m) => {
                  // Quote-level misses (ship_to / order_services) aren't tied to
                  // an item, so they skip the suffix. Same catalog key the other
                  // summary uses, so the wording can't drift between the lists.
                  const suffix =
                    !m.scope && drafts && drafts.length > 1
                      ? t('edit.projectSuffix', { n: m.line_index + 1 })
                      : '';
                  return (
                    <li key={`${m.line_local_id}-${m.field_key}`}>
                      {m.scope === 'unanchored' ? (
                        // Named, never clickable: this page renders no control
                        // for the key, so a scroll link would go nowhere — and
                        // the reason rides here, since no inline error can.
                        <span>{m.detail ?? m.field_label}</span>
                      ) : (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => revealMiss(m)}
                        >
                          {m.field_label}
                          {suffix}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              {fieldSummary.trailing && (
                <p style={{ margin: '12px 0 0' }}>{fieldSummary.trailing}</p>
              )}
            </div>
          ) : bannerError ? (
            // No field-level breakdown to show — surface the generic
            // error in the same location as field-level errors so the
            // customer reads action feedback in one consistent place.
            <div
              id="action-feedback"
              className="banner banner--error"
              role="alert"
              aria-live="polite"
              style={{ marginBottom: 16 }}
            >
              {bannerError}
            </div>
          ) : savedNotice ? (
            // Explicit-save confirmation. Lives in the same near-button
            // slot as error feedback so the customer reads all action
            // outcomes in one consistent place.
            <div
              id="action-feedback"
              className="banner banner--success"
              role="status"
              aria-live="polite"
              style={{ marginBottom: 16 }}
            >
              {t('edit.savedConfirmation')}
            </div>
          ) : null}
          {changeOrderOpen ? (
            // Rep-facing: each save re-sends this placed order to Nexus, and the
            // partner re-signs there. Nothing here starts a checkout.
            <p className="help" style={{ marginBottom: 16 }}>
              {t('edit.changeOrderNote')}
            </p>
          ) : repUnlocked ? (
            // Rep-facing: the order is rep-authored and still open in Nexus, so
            // each save re-imports it into the same Opportunity.
            <p className="help" style={{ marginBottom: 16 }}>
              {t('edit.salesQuoteRepNote')}
            </p>
          ) : (
            salesControlled && (
              // Sales is preparing this quote, so the partner can keep editing
              // and saving — but pricing comes from their rep, not the portal.
              <p className="help" style={{ marginBottom: 16 }}>
                {t('edit.salesControlledNote')}
              </p>
            )
          )}
          {hasBoundBookLine && (
            // Step 10: an insert is printed as its own project, specced and
            // priced like any other piece; on a coil or loose-leaf book the
            // Inserts finishing touch then says where it goes in the book.
            <p className="help" style={{ marginBottom: 16 }}>
              {t('edit.boundBookNote')}
            </p>
          )}
          {hasHardcoverLine && (
            // Step 11: a dust jacket is its own printed piece, not a field on
            // the hardcover book. Guide the customer to the "Add another
            // project" control below — adding a project runs the same
            // configurator on a new line, where they spec + upload the jacket
            // like any other piece.
            <p className="help" style={{ marginBottom: 16 }}>
              {t('edit.hardcoverNote')}
            </p>
          )}
          {/* "Quote now, art to follow" consequence warning. The toggle itself
              now lives up next to the artwork uploads (kept quiet there); this
              surfaces the "subject to change" caveat near the price + action
              buttons, and only once the customer has actually opted in. */}
          {artworkPending && (
            <div className="banner banner--warn" style={{ marginBottom: 16 }}>
              <strong>{t('edit.artworkPendingWarning.title')}</strong>
              <p style={{ margin: '8px 0 0' }}>
                {t('edit.artworkPendingWarning.body')}
              </p>
              {/* The escape hatch, and only for marks the customer has no other
                  way to clear: a marked item that is collapsed (or no longer
                  shows upload slots) leaves this warning on screen with no
                  checkbox anywhere to clear it. Unticking clears those items'
                  marks; a mark on a slot still on screen stays with its slot. */}
              {isEditable && unreachableArtworkMarkLines.length > 0 && (
                <label className="artwork-slot__pending-toggle">
                  <input
                    type="checkbox"
                    checked
                    onChange={(e) => {
                      if (e.target.checked) return;
                      const ids = new Set(unreachableArtworkMarkLines.map((d) => d.local_id));
                      setDrafts((cur) =>
                        (cur ?? []).map((d) =>
                          ids.has(d.local_id)
                            ? { ...d, config: withoutArtworkPending(d.config) }
                            : d
                        )
                      );
                    }}
                  />
                  <span className="help">{t('uploadSlot.pendingToggle.label')}</span>
                </label>
              )}
            </div>
          )}
          <div className="actions">
            {/* The autosave's quiet read-back. Its box is sized by the row, not
                by its text, so the wording changing never moves the buttons. */}
            <span className="autosave-status" role="status" aria-live="polite">
              {autosaveStatus === 'saving'
                ? t('edit.autosave.saving')
                : autosaveStatus === 'saved'
                  ? t('edit.autosave.saved')
                  : autosaveStatus === 'error'
                    ? t('edit.autosave.failed')
                    : null}
            </span>
            <button
              type="button"
              className="primary"
              onClick={() => {
                setValidationRevealSeq((n) => n + 1);
                void handleSave();
              }}
              disabled={saving}
            >
              {saving ? t('edit.cta.saving') : t('edit.cta.save')}
            </button>
            {showSeeMyPrice && (
              <button
                type="button"
                className="primary"
                onClick={() => void handleGetPricing()}
                disabled={saving || pricingState.kind === 'pricing'}
                aria-describedby={
                  (fieldSummary && fieldSummary.misses.length > 0) || bannerError
                    ? 'action-feedback'
                    : undefined
                }
              >
                {pricingState.kind === 'pricing'
                  ? pricingLocked
                    ? t('edit.cta.submitting')
                    : t('edit.cta.calculating')
                  : primaryPriceCtaLabel}
              </button>
            )}
            {/* Two actions: add a blank project, or copy the LAST project — the
                fast path for "another piece just like the one I just did, tweak
                a detail". Artwork does not carry over (uploads live in Asset
                rows, not the copied config). */}
            <AddProjectActions
              disabled={saving}
              copyTargetLabel={
                drafts.length > 0
                  ? drafts[drafts.length - 1]!.title ||
                    deriveProjectName(schema, drafts[drafts.length - 1]!.config) ||
                    t('shared.untitledProject')
                  : null
              }
              onAdd={() => void addLine()}
              onCopyTarget={() => {
                const last = drafts[drafts.length - 1];
                if (last) void duplicateLine(last.local_id);
              }}
            />
            {pricingAllowed && !pricingLocked && quote.status === 'priced' && (
              <button
                type="button"
                className="primary"
                onClick={() => void handleSubmit()}
                disabled={submitState.kind === 'submitting'}
              >
                {submitState.kind === 'submitting'
                  ? t('edit.cta.placingOrder')
                  : t('edit.cta.placeOrder')}
              </button>
            )}
          </div>
        </div>
      )}

      {submitState.kind === 'repriced' && (
        <div className="banner banner--warn">
          <strong>{t('edit.priceChanged')}</strong>
          <p style={{ margin: '8px 0' }}>{submitState.message}</p>
          <p>
            <Trans
              i18nKey="edit.priceChangeDetail"
              t={t}
              values={{
                was: formatCurrency(submitState.previous, quote.currency ?? 'USD'),
                now: formatCurrency(submitState.next, quote.currency ?? 'USD'),
              }}
              components={[<strong key="now" />]}
            />
          </p>
          <p>{t('edit.tapToConfirm', { label: primaryPriceCtaLabel })}</p>
        </div>
      )}

      {submitState.kind === 'pending_approval' && (
        <div className="banner banner--warn">
          <strong>{t('edit.reviewing.title')}</strong>
          <p>{submitState.message}</p>
          <p>{t('edit.reviewing.body')}</p>
          <p style={{ margin: '8px 0 0' }}>
            <Trans
              i18nKey="edit.contactSupport"
              t={t}
              values={{ email: contactSalesRep.email, phone: SUPPORT_PHONE }}
              components={[
                <a key="email" href={`mailto:${contactSalesRep.email}`} />,
                <a key="phone" href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`} />,
              ]}
            />
          </p>
        </div>
      )}

      {/* Saved-price snapshot — shown on reload (pricingState resets to
          idle) so the customer sees their last itemized price, finishing
          sub-lines included, reconciling to the saved total. A fresh
          "See my price" replaces it with the live PricingBlock below.
          Suppressed when Nexus owns the quote, or when an approved quote is
          already shown above — both supersede the partner's old self-price.
          Also suppressed under the pricing lockdown — no price surfaces. Shown
          to the rep under a change order, where the saved price is the
          re-priced order rather than the partner's old self-price. */}
      {pricingState.kind === 'idle' &&
        !pricingLocked &&
        (changeOrderOpen || (!salesControlled && !quoteStatus?.approved_quote)) && (
          <SavedPriceSummary quote={quote} />
        )}

      {/* Suppressed once a rep has taken the quote over (control='sales'):
          the rep is now authoring the price, so a stale customer-side
          "See my price" result must not keep showing alongside the
          "your Argo rep is preparing the price" message. Mirrors the
          SavedPriceSummary guard above. EXCEPTION: under the pricing lockdown
          the "See my price" click yields the contact-sales handoff, which we DO
          want to render even for a sales-controlled quote (the lock supersedes),
          and the rep's own re-price under a change order. */}
      {pricingState.kind === 'priced' && (!salesControlled || pricingLocked || changeOrderOpen) && (
        <div ref={pricingBlockRef} className="scroll-target">
          {customQuoteConfirmed ? (
            // The customer clicked "Submit for quote" (this config already
            // came back CANNOT_PRICE once) — confirm the resubmit landed
            // instead of repeating the generic contact-sales banner verbatim.
            <div className="banner banner--success">
              <strong>{t('edit.submitted')}</strong>
              <p style={{ margin: '8px 0 0' }}>{t('edit.submittedFollowUp')}</p>
              <p style={{ margin: '8px 0 0' }}>
                <Trans
                  i18nKey="edit.contactSupportSooner"
                  t={t}
                  values={{ email: contactSalesRep.email, phone: SUPPORT_PHONE }}
                  components={[
                    <a key="email" href={`mailto:${contactSalesRep.email}`} />,
                    <a key="phone" href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`} />,
                  ]}
                />
              </p>
            </div>
          ) : (
            <PricingBlock
              response={pricingState.response}
              show_terms={
                quote.status === 'submitted' ||
                quote.status === 'pending_approval' ||
                quote.status === 'approved'
              }
              // "Quote now, art to follow" — render the disclaimer under the
              // live total when the toggle is set (uses the live state so it
              // reflects an in-session toggle, not just the persisted flag).
              artwork_pending={artworkPending}
            />
          )}
        </div>
      )}

      {identityPrompt.kind === 'open' && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="identity-prompt-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <form
            className="card"
            style={{ width: 'min(440px, 92vw)', margin: 0 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (identityPrompt.kind !== 'open') return;
              const name = identityPrompt.name.trim();
              if (!name) return;
              const payload: submissionApi.ContactUserIdentityInput = { name };
              const company = identityPrompt.company_name.trim();
              if (company) payload.company_name = company;
              void handleSubmit(payload);
            }}
          >
            <h2 id="identity-prompt-title" style={{ marginTop: 0 }}>
              {t('edit.identity.title')}
            </h2>
            <p style={{ marginTop: 0 }}>{identityPrompt.message}</p>
            <div className="form-row">
              <label htmlFor="identity-name">{t('edit.identity.nameLabel')}</label>
              <input
                id="identity-name"
                type="text"
                autoFocus
                required
                value={identityPrompt.name}
                onChange={(e) =>
                  setIdentityPrompt((s) =>
                    s.kind === 'open' ? { ...s, name: e.target.value } : s,
                  )
                }
              />
            </div>
            <div className="form-row">
              <label htmlFor="identity-company">{t('edit.identity.companyLabel')}</label>
              <input
                id="identity-company"
                type="text"
                placeholder={t('edit.identity.companyHint')}
                value={identityPrompt.company_name}
                onChange={(e) =>
                  setIdentityPrompt((s) =>
                    s.kind === 'open' ? { ...s, company_name: e.target.value } : s,
                  )
                }
              />
              <small>{t('edit.identity.companyNote')}</small>
            </div>
            <div className="actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setIdentityPrompt({ kind: 'closed' })}
                disabled={submitState.kind === 'submitting'}
              >
                {t('edit.identity.cancel')}
              </button>
              <button
                type="submit"
                className="primary"
                disabled={
                  submitState.kind === 'submitting' ||
                  identityPrompt.name.trim().length === 0
                }
              >
                {submitState.kind === 'submitting' ? t('edit.cta.placingOrder') : t('edit.cta.placeOrder')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );

  async function handleSubmit(
    contact_user_identity?: submissionApi.ContactUserIdentityInput,
  ): Promise<void> {
    setValidationRevealSeq((n) => n + 1);
    setSubmitState({ kind: 'submitting' });
    setBannerError(null);
    setFieldErrors({});
    setFieldSummary(null);
    // An autosave already in flight (the focus change that came with this click)
    // lands before this order's own write and submit, never between them.
    await saveChainRef.current;
    // A refused save left the server holding an older version than the page
    // shows, and placing now would hand Nexus that older one. Saving again shows
    // the refusal against its field; a save that now succeeds changes the lines,
    // so the order needs its price again before it can be placed.
    if (lastSaveFailedRef.current) {
      await handleSave({ silent: true });
      setSubmitState({ kind: 'idle' });
      return;
    }

    // Full per-line form validation — the SAME shared collector Save and the
    // price pre-flight run (collectConfigMisses: required config fields + required
    // uploads, INCLUDING the conditional variable-data spreadsheet now that it's
    // in the shared upload validator). Submit reuses it rather than repeating any
    // per-rule check, so it can never enforce something Save didn't already
    // report — Save reports (permissive), submit blocks (a place-order action).
    // Runs once the asset list has loaded so a customer who already uploaded is
    // never falsely blocked; if it hasn't, the authoritative backend gates cover
    // it. (The quote-level ship-to / order-services / proof gates below are
    // place-order requirements on quote state, not per-line form fields, so they
    // stay submit-only.)
    if (assetsQuery.data !== undefined) {
      const misses = collectConfigMisses(
        schema,
        drafts!,
        presentRoles,
        mediaContentQuery.data ?? null,
      );
      if (misses.length > 0) {
        const errs: Record<string, string> = {};
        for (const m of misses) {
          errs[lineFieldKey(m.line_local_id, m.field_key)] = missInlineMessage(m);
        }
        setFieldErrors(errs);
        setFieldSummary({
          source: 'submit',
          heading: t('edit.placeNeedDetail', { count: misses.length }),
          misses,
        });
        // Same repair path the price pre-flight gives: scroll to the first
        // offending control instead of leaving the customer to hunt for it.
        revealMiss(misses[0]!);
        setSubmitState({ kind: 'idle' });
        return;
      }
    }

    // Ship-to pre-block (step 12): a delivery mode must be picked before placing
    // the order — no card lit reports DELIVERY_MODE_FIELD_KEY on the cards — and a
    // Shipping order needs a full address. Surfaces the SAME inline + near-button +
    // reveal/scroll UX as the gates above (field-keyed). The backend gate
    // (assertShipToReady) is authoritative; this spares a round trip and COMPOSES
    // with — never bypasses — the status==='priced' / step-8 gates. Uses the
    // shared validator so client + backend can't disagree.
    // Combined with the order-services pre-block (step 14): when Shipping is
    // selected the destination address is required (shipToMissingFieldKeys);
    // when Fulfillment is selected its details are required
    // (orderServicesMissingFieldKeys). Both produce field-keyed misses
    // (ship_to.* / fulfillment.*) resolved by applyFieldKeyFailure. An assembly
    // item's kit is judged with the rest of its line config (lineConfigIssues).
    // The backend gates (assertShipToReady + assertOrderServicesReady) are
    // authoritative; this spares a round trip and COMPOSES with the status===
    // 'priced' / step-8 gates. Uses the shared validators so client + backend
    // can't disagree.
    const serviceMissing = [
      // Every address, not just the first, AND the sum of their piece counts —
      // the SAME shared validator the authoritative backend gate runs, given the
      // same order quantity, so the two cannot disagree about which address is
      // incomplete or which key names it.
      ...shipToMissingFieldKeys(shipTos, {
        services: orderServices,
        orderQuantity: splitOrderQuantity,
      }),
      // The printed sample (FB-0036): how many, and where each is mailed. The
      // SAME shared validator the authoritative gate runs, so the two cannot
      // disagree about which sample address is incomplete or whether the counts
      // add up — and it balances them against the SAMPLE count, never the order
      // quantity. Silent unless the quote resolves to a physical proof.
      ...proofDeliveryMissingFieldKeys({
        proof_type: proofTypeResolved,
        proof_quantity: proofQuantity,
        proof_ship_tos: proofShipTos,
      }),
      ...orderServicesMissingFieldKeys(orderServices, {
        lines: (drafts ?? []).map((d) => ({ id: d.local_id, config: d.config, title: d.title })),
      }),
    ];
    // Item 6b — mandatory-barcode pre-block on a fulfilled order. Auto-satisfied
    // when any uploaded artwork scanned as carrying a barcode; otherwise the
    // customer must answer the resolution question. Skipped while the asset list
    // is still loading (assetsQuery.data undefined) — the authoritative backend
    // gate covers that window, exactly like the variable-data pre-block.
    if (assetsQuery.data !== undefined) {
      const barcodeDetected = assetsQuery.data.some((a) => a.barcode_scan === 'found');
      if (orderBarcodeResolutionMissing(orderServices, { barcodeDetected })) {
        serviceMissing.push(BARCODE_RESOLUTION_FIELD_KEY);
      }
    }
    if (serviceMissing.length > 0) {
      applyFieldKeyFailure({
        source: 'submit',
        heading: t('edit.error.serviceMissing'),
        fieldKeys: serviceMissing,
        message: t('edit.fillHighlighted'),
      });
      setSubmitState({ kind: 'idle' });
      return;
    }

    // Proof pre-block: a proof is mandatory (a digital proof is the default, a
    // physical proof an optional upgrade). Block when the quote resolves to no
    // proof — cleared on every line, or a legacy 'none'. The backend gate
    // (assertProofSelected) is authoritative; this spares a round trip and uses
    // the SAME shared resolver so client + backend can't disagree. Field-keyed
    // ('proof_type') so it surfaces inline + near-button like the gates above.
    // (The pricing pre-flight already blocks the normal path — a quote can't
    // reach 'priced' without a proof — so this mainly catches a legacy quote
    // that was priced before proof became required.)
    // Skipped entirely when there is nothing on the order to proof:
    // resolveQuoteProofType always resolves 'none' with no line's proof_type to
    // read, and `proof_type` is not OFFERED to every family. An empty draft is
    // one such shape; so is an order made only of `assembly` / `something_else`
    // items, which have no proof field
    // anywhere for the customer to answer — blocking there would strand them on an
    // error with nothing on the page to clear it (ADR-019). The SAME shared
    // predicate the backend's assertProofSelected caller uses.
    if (
      quoteHasProofableLine(drafts!.map((d) => d.config)) &&
      resolveQuoteProofType(drafts!.map((d) => d.config)) === 'none'
    ) {
      applyFieldKeyFailure({
        source: 'submit',
        heading: t('edit.error.serviceMissing'),
        fieldKeys: ['proof_type'],
        message: t('edit.proofIntro'),
      });
      setSubmitState({ kind: 'idle' });
      return;
    }

    // Persist the ship-to + order services, and where each item is stocked,
    // WITHOUT sending `lines`, so saving them never invalidates the price
    // snapshot (none is part of the priced total — ADR-010 §12) and a stale tab
    // cannot drop a line it never loaded. The stocking targets go as a
    // targets-only write naming just the saved lines whose answer differs from
    // the server's. The backend re-checks all of it authoritatively on submit.
    try {
      await quotesApi.updateQuote(id!, {
        ship_tos: shipTos,
        proof_quantity: proofQuantity,
        proof_ship_tos: proofShipTos,
        order_services: orderServices,
        line_fulfillment_targets: changedFulfillmentTargets(
          drafts!.map((d) => ({ id: d.local_id, config: d.config })),
          quoteQuery.data?.lines ?? [],
        ),
      });
    } catch (err) {
      // The same save boundary as Save, so a refusal points at its field the
      // same way rather than as a bare banner.
      const envelope = extractApiError(err);
      const message = envelope?.message ?? t('edit.error.deliverySaveFailed');
      const fieldKeys = readFieldKeys(envelope?.details);
      if (fieldKeys && fieldKeys.length > 0) {
        applyFieldKeyFailure({
          source: 'submit',
          heading: t('edit.error.placeReview'),
          fieldKeys,
          message,
          details: envelope?.details,
        });
      } else {
        setBannerError(message);
      }
      setSubmitState({ kind: 'idle' });
      return;
    }

    try {
      const result = await submissionApi.submitQuote(id!, contact_user_identity);
      if (!result.ok) {
        // Same posture as save + pricing: if the server flagged
        // specific fields, render inline + near-button. Otherwise drop
        // to the generic near-button banner.
        const fieldKeys = readFieldKeys(result.failure.details);
        if (fieldKeys && fieldKeys.length > 0) {
          applyFieldKeyFailure({
            source: 'submit',
            // Both branches read identically today — a single key, not a
            // spurious plural pair.
            heading: t('edit.error.placeReview'),
            fieldKeys,
            message: result.failure.message,
          });
        } else if (result.failure.fallback_action === 'contact_sales') {
          // A refusal that sends the partner to sales names who to contact: the
          // rep Nexus supplied, else the shared default, unless the message
          // already names them.
          const rep = result.failure.sales_rep ?? DEFAULT_SALES_REP;
          setBannerError(
            result.failure.message.includes(rep.email)
              ? result.failure.message
              : `${result.failure.message} ${t('edit.error.contactRep', { name: rep.name, email: rep.email })}`,
          );
        } else {
          setBannerError(result.failure.message);
        }
        setSubmitState({ kind: 'idle' });
        return;
      }
      // Every non-failure outcome, not just 'submitted' — a repriced or
      // pending-approval submit IS an attempt that reached Nexus, and counting
      // only the clean case hides exactly the friction this event exists to
      // measure. `kind` is a closed union.
      trackEvent(ANALYTICS_EVENTS.quote_submitted, { outcome: result.submission.kind });
      switch (result.submission.kind) {
        case 'submitted':
          await quoteQuery.refetch();
          if (result.submission.requires_payment) {
            navigate(`/quotes/${id}/checkout`);
          }
          setSubmitState({ kind: 'idle' });
          setIdentityPrompt({ kind: 'closed' });
          return;
        case 'repriced':
          await quoteQuery.refetch();
          setSubmitState({
            kind: 'repriced',
            previous: result.submission.previous_total_sale_amount,
            next: result.submission.new_total_sale_amount,
            message: result.submission.message,
          });
          return;
        case 'pending_approval':
          await quoteQuery.refetch();
          setSubmitState({ kind: 'pending_approval', message: result.submission.message });
          setIdentityPrompt({ kind: 'closed' });
          return;
        case 'identity_required':
          // First submit by a contact user without a Nexus relationship.
          // Open the modal to capture name + optional company; the modal's
          // save handler calls handleSubmit again with the identity.
          setSubmitState({ kind: 'idle' });
          setIdentityPrompt({
            kind: 'open',
            message: result.submission.message,
            name: '',
            company_name: '',
          });
          return;
      }
    } catch (err) {
      const envelope = extractApiError(err);
      const message = envelope?.message ?? t('edit.error.submitFailed');
      const fieldKeys = readFieldKeys(envelope?.details);
      if (fieldKeys && fieldKeys.length > 0) {
        applyFieldKeyFailure({
          source: 'submit',
          heading: t('edit.error.placeReview'),
          fieldKeys,
          message,
          details: envelope?.details,
        });
      } else {
        setBannerError(message);
      }
      setSubmitState({ kind: 'idle' });
    }
  }
}

// Shared "?" definition shown on BOTH the Order header and each Project header.
// The two fields read alike when an order has a single project, so spell out the
// difference once and reuse it in both spots — the customer gets the same answer
// wherever they're looking when the question occurs to them.
// A function component (not a module-level JSX constant) so it can call
// useTranslation — hooks only work inside components.
function OrderVsProjectHelp(): JSX.Element {
  const { t } = useTranslation('quote');
  return (
    <>
      <p className="help-affordance__field">
        <Trans
          i18nKey="edit.help.order"
          t={t}
          components={[<strong key="term" />, <span key="eg" className="entity-header__help-eg" />]}
        />
      </p>
      <p className="help-affordance__field">
        <Trans
          i18nKey="edit.help.project"
          t={t}
          components={[<strong key="term" />, <span key="eg" className="entity-header__help-eg" />]}
        />
      </p>
    </>
  );
}

// Shared header for the two nameable entities — the whole Order and each Project
// within it. One shape so the customer reads them as parallel: a small uppercase
// eyebrow naming the level (carrying the "?" that explains how the two differ),
// then the live name as the prominent heading. The heading mirrors whatever the
// customer types into the field below in real time, so there's no separate
// static label — the heading IS the label. `badges` ride inline after the name
// (status tags); `trailing` holds the row's own action (Back to list / Remove).
function EntityNameHeader({
  eyebrow,
  name,
  level,
  badges,
  trailing,
  collapsed,
  onToggleCollapsed,
  bodyId,
}: {
  eyebrow: string;
  name: string;
  level: 'h2' | 'h3';
  badges?: ReactNode;
  trailing?: ReactNode;
  // Disclosure state for the section this header titles. Supplied only where the
  // header owns a collapsible body (each project); the Order card passes none and
  // renders no chevron.
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  // The body element the chevron shows/hides, for aria-controls. Always present
  // in the DOM (collapsed swaps the configurator for a one-line recap), so this
  // can be a static reference.
  bodyId?: string;
}): JSX.Element {
  const { t } = useTranslation('quote');
  const [helpOpen, setHelpOpen] = useState(false);
  const panelId = useId();
  const Heading = level;
  return (
    <div className="entity-header">
      <div className="entity-header__bar">
        {onToggleCollapsed && (
          <button
            type="button"
            className="disclosure-toggle entity-header__disclosure"
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            // Named by the eyebrow ("Item 2") so a multi-project quote doesn't
            // present N identically-labelled chevrons to assistive tech.
            aria-label={
              collapsed
                ? t('edit.collapse.expandAria', { eyebrow })
                : t('edit.collapse.collapseAria', { eyebrow })
            }
            onClick={onToggleCollapsed}
          >
            <ChevronDownIcon className="disclosure-toggle__chevron" />
          </button>
        )}
        <div className="entity-header__titles">
          <span className="entity-header__eyebrow">
            {eyebrow}
            <button
              type="button"
              className="help-affordance__toggle"
              aria-expanded={helpOpen}
              // Only reference the panel while it's mounted — the div below
              // renders only when open, so a static aria-controls would point at
              // a missing id when collapsed. Prefixed with the eyebrow so each
              // header's "?" has a distinct accessible name on a multi-project
              // quote (the visible eyebrow + heading disambiguate sighted users).
              aria-controls={helpOpen ? panelId : undefined}
              aria-label={
                helpOpen
                  ? t('edit.help.hideDiffAria', { eyebrow })
                  : t('edit.help.showDiffAria', { eyebrow })
              }
              onClick={() => setHelpOpen((v) => !v)}
            >
              <span aria-hidden="true">?</span>
            </button>
          </span>
          <Heading className="entity-header__name">
            {name}
            {badges}
          </Heading>
        </div>
        {trailing && <div className="entity-header__trailing">{trailing}</div>}
      </div>
      {helpOpen && (
        <div className="help-affordance__panel entity-header__help" id={panelId} role="note">
          <OrderVsProjectHelp />
        </div>
      )}
    </div>
  );
}

// The two add-an-item actions as a pair of plain buttons: "Copy <item>" on the
// left (secondary skin) and "Add another item" on the right (accent). Stateless
// — the actions themselves (addLine / duplicateLine) live in QuoteEdit.
function AddProjectActions({
  copyTargetLabel,
  disabled,
  onAdd,
  onCopyTarget,
  copyScope = 'last',
}: {
  // Name of the project the copy action duplicates (typed title → spec-derived
  // name → fallback), shown on the copy button so the customer knows what
  // they're copying — the LAST project for the actions-row mount, THIS one for
  // an item's own footer. null when the quote has no projects — only an empty
  // draft, since every door seeds an item and the last one cannot be removed —
  // which drops the copy button entirely and switches the add label to "Add an
  // item".
  copyTargetLabel: string | null;
  disabled: boolean;
  onAdd: () => void;
  onCopyTarget: () => void;
  // Which project the copy action duplicates, which is the only thing that
  // differs between the two mounts: the actions-row control copies the LAST
  // project, each item's own footer control copies THAT item.
  copyScope?: 'last' | 'this';
}): JSX.Element {
  const { t } = useTranslation('quote');

  return (
    <div className="add-project-actions">
      {copyTargetLabel && (
        <button type="button" onClick={onCopyTarget} disabled={disabled}>
          {t(copyScope === 'this' ? 'edit.copyThisProject' : 'edit.copyLastProject', {
            name: copyTargetLabel,
          })}
        </button>
      )}
      <button type="button" className="primary" onClick={onAdd} disabled={disabled}>
        {/* "Add ANOTHER item" is a lie on a quote that has none, and an empty
            draft still loads — this is how its customer reaches the grid. */}
        {t(copyTargetLabel ? 'edit.cta.addProject' : 'edit.cta.addFirstProject')}
      </button>
    </div>
  );
}

// One project's editor: the configurator form + its artwork. Extracted so it
// can own the per-line asset state (useLineAssets) — a hook can't run inside
// the drafts.map — and pass that ONE state to BOTH the inline cover/guts/
// hardcover upload slots (rendered inside SchemaForm) and the residual
// "Artwork for <item>" block (ArtworkUploader). Sharing one instance keeps the
// two surfaces consistent: an upload/delete in either shows in both, and every
// change fires onAssetsChanged so the quote-wide variable-data submit pre-block
// stays current.
function LineEditor({
  schema,
  draft,
  index,
  quoteId,
  isEditable,
  impersonating,
  isHistorical,
  quoteTitle,
  fieldErrors,
  canRemove,
  onPatch,
  kitLines,
  hasUnsavedLines,
  onRequestSave,
  onPersistMediaUploadChoice,
  ruleErrorRevealSeq,
  onRemove,
  onAssetsChanged,
  collapsed,
  onToggleCollapsed,
  footer,
  renderAfterGroup,
  entryDivisionKey,
  barcodeRequired,
}: {
  schema: FormSchemaDto;
  draft: LineDraft;
  index: number;
  quoteId: string;
  isEditable: boolean;
  impersonating: boolean;
  // Past-order clone. Drives the softer "files didn't carry over" asset note and
  // the unmapped-product context callout when this line's product type is blank.
  isHistorical: boolean;
  quoteTitle: string;
  fieldErrors: Record<string, string>;
  canRemove: boolean;
  onPatch: (patch: Partial<LineDraft>) => void;
  // The quote's other projects a kit content row (the item's own AssemblyPanel)
  // or a book's insert row (the configurator) may point at, and whether any of
  // them is still unsaved.
  kitLines: readonly KitContentLineOption[];
  hasUnsavedLines: boolean;
  // Adding a row to THIS item's kit persists the quote — an item's kit rows
  // ride in its config, which only a save writes.
  onRequestSave: () => void;
  // Saves the order ahead of a new media content upload (persistMediaUploadChoice).
  onPersistMediaUploadChoice: () => Promise<boolean>;
  // Passed through to SchemaForm — bumped by a Save, See my price or Place my
  // order click (never an autosave), it fixes which validation-rule errors show.
  ruleErrorRevealSeq: number;
  onRemove: () => void;
  onAssetsChanged: () => void;
  // Disclosure state, owned by QuoteEdit so the missing-field summary can expand
  // the item it needs to scroll to. Collapsed swaps the configurator for a
  // one-line recap and drops the spec aside, so a long multi-item quote stays
  // scannable.
  collapsed: boolean;
  onToggleCollapsed: () => void;
  // The "add another item" control that closes out this item's card, so the
  // affordance sits wherever the customer finishes an item rather than only at
  // the foot of the whole order.
  footer?: ReactNode;
  // Passed through to SchemaForm — places the quote-level Need-By card after the
  // Proofs section. Supplied for every line; it renders for the first one only
  // (see QuoteEdit).
  renderAfterGroup?: (groupKey: string | null) => ReactNode;
  // Passed through to SchemaForm — the quote's entry door, which decides whether
  // a door-curated product card is offered. No option is curated today (see
  // QuoteEdit).
  entryDivisionKey: string | null;
  // Quote-level fulfillment signal (orderRequiresBarcode) → the artwork barcode
  // advisory reads "required for fulfillment" instead of "optional". Quote-level
  // because warehousing/fulfillment is chosen once for the whole order, so it
  // adjusts every line's notice live as the customer toggles that service.
  barcodeRequired: boolean;
}): JSX.Element {
  const { t } = useTranslation('quote');
  // Artwork attaches to a persisted QuoteLine — a freshly-added (unsaved) line
  // can't take uploads yet, so inline slots are suppressed and the residual
  // block shows the "save first" note. addLine auto-saves, so this flips true
  // quickly.
  const canUpload = !isUnsavedLine(draft.local_id);
  const lineAssets = useLineAssets({
    quote_id: quoteId,
    quote_line_id: draft.local_id,
    disabled: !isEditable,
    isHistorical,
    onAssetsChanged,
  });
  // "Quote now, art to follow", one mark per artwork slot, stored on this line's
  // config. Each slot reads and writes only its own mark, so a ticked cover says
  // nothing about the manuscript.
  const pendableArtworkRoles = useMemo(
    () => new Set(linePendableArtworkRoles(draft.config)),
    [draft.config]
  );
  const pendingArtworkRoles = useMemo(() => linePendingArtworkRoles(draft.config), [draft.config]);
  const artworkPendingToggle: ArtworkPendingToggleFor = (role) =>
    pendableArtworkRoles.has(role)
      ? {
          pending: pendingArtworkRoles.has(role),
          ...(isEditable
            ? {
                onChange: (next: boolean) =>
                  onPatch({ config: withArtworkPendingRole(draft.config, role, next) }),
              }
            : {}),
        }
      : null;
  // The file arriving answers the question: once a marked slot's artwork lands
  // on this line, its mark comes off.
  const lineAssetRoles = useMemo(
    () => new Set(lineAssets.assets.map((a) => a.component_role)),
    [lineAssets.assets]
  );
  useEffect(() => {
    if (!isEditable || pendingArtworkRoles.size === 0) return;
    const next = withoutLandedArtworkMarks(draft.config, lineAssetRoles);
    if (next !== draft.config) onPatch({ config: next });
  }, [isEditable, pendingArtworkRoles, lineAssetRoles, draft.config, onPatch]);
  // An assembly item's reference photos, owned here rather than by its panel so
  // the spec rail can wear the finished-kit photo. Every other family passes no
  // quote, which makes the hook fetch nothing. An unsaved line passes no line,
  // which keeps the photo controls closed until it has a real id — the upload
  // route refuses a synthetic local_id.
  const assemblyPhotos = useAssemblyPhotos({
    quote_id: isAssemblyLine(draft) ? quoteId : undefined,
    quote_line_id: canUpload ? draft.local_id : null,
    disabled: !isEditable,
  });
  // Live artwork thumbnail for the right-rail mockup — booklet families wear the
  // customer's uploaded cover, single sheet its flat artwork, and an assembly
  // item its finished-kit photo; families with no paint-on face stay null and
  // the mockup shows its branded per-type placeholder.
  const artworkUrl = useArtworkPreview(
    lineAssets,
    (draft.config.product_type as FamilyCode | undefined) ?? null,
    assemblyPhotos,
  );

  // Intuitive auto-name derived live from the spec (product + size, e.g.
  // "Perfect-bound Book — 6″ × 9″"; "Dust jacket" for that preset). Shown as the
  // Project-name placeholder and used as the display fallback wherever the
  // project is referenced when the customer hasn't named it themselves. Pure
  // display — never written into draft.title — so it keeps tracking the spec as
  // the customer fills it in.
  const derivedName = deriveProjectName(schema, draft.config);
  const displayName = draft.title || derivedName || t('shared.untitledProject');
  // This item's own assembly snapshot, read straight off its config. A plain
  // read of stored data, never a derivation of anything that moves (R26).
  const itemAssembly = configAssembly(draft.config);
  const itemLabel = draft.title || derivedName || t('edit.projectN', { n: index + 1 });

  // The shared fieldErrors map is line-namespaced (`<local_id>::<field_key>`)
  // so it can carry several projects' misses without collision. SchemaForm
  // still expects errors keyed by bare field key, so strip this line's prefix
  // back off — only entries for THIS project survive, so the inline error +
  // red box light up only where the field is actually missing.
  const prefix = `${draft.local_id}::`;
  const bodyId = `project-line-body-${draft.local_id}`;
  // The item-name control is hand-written here rather than rendered by
  // SchemaForm (the name is a QuoteLine column, not a schema field), so it wires
  // its own id + inline error — using the SAME line-namespaced key the summary's
  // scroll target is built from, or "jump to the first problem" would land
  // nowhere.
  const titleFieldId = `${draft.local_id}::${LINE_TITLE_FIELD_KEY}`;
  const titleErrorId = `${titleFieldId}-error`;
  const lineErrors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(fieldErrors)) {
      if (k.startsWith(prefix)) out[k.slice(prefix.length)] = v;
    }
    return out;
  }, [fieldErrors, prefix]);
  const missCount = Object.keys(lineErrors).length;
  const titleError = lineErrors[LINE_TITLE_FIELD_KEY];

  // Asks first, by name, like every other delete in the portal. Removing an item
  // moves the next one up into its place, Remove button included, so without the
  // question a second click deletes an item nobody chose. Native confirm() on
  // purpose: a Modal portals outside the editor root, so its OK click is not
  // seen as the customer's edit and the autosave folds the removal into its
  // baseline instead of saving it.
  function confirmRemove(): void {
    if (confirm(t('edit.confirmRemove', { n: index + 1, name: displayName }))) onRemove();
  }

  return (
    <div
      id={`project-line-${index}`}
      // Programmatic focus target for the review row's "Edit item N" (and the
      // add/copy scrolls) — never in the tab order, so it costs the customer no
      // extra stop while walking the form.
      tabIndex={-1}
      className={
        collapsed
          ? 'line-editor configurator-layout line-editor--collapsed scroll-target'
          : 'line-editor configurator-layout scroll-target'
      }
    >
      <div className="configurator-layout__main">
        {/* The "Project N" eyebrow keeps the number visible (the missing-field
            summary references "project N"), while the heading shows the project's
            live name — the customer's typed title, falling back to the
            spec-derived name, then "Untitled project". It mirrors the field below
            as they type, so there's no separate static "Project name" label. */}
        <EntityNameHeader
          eyebrow={t('edit.projectN', { n: index + 1 })}
          level="h3"
          name={displayName}
          trailing={
            // An order must stay orderable: the Remove action appears once a
            // second project exists, so the last one can never be removed.
            isEditable && canRemove ? (
              <button
                type="button"
                className="danger"
                onClick={confirmRemove}
                aria-label={t('edit.removeAria', { eyebrow: t('edit.projectN', { n: index + 1 }) })}
              >
                {t('edit.cta.remove')}
              </button>
            ) : undefined
          }
          collapsed={collapsed}
          // Withheld until the line has its persisted id. collapsedLines is keyed
          // on local_id, and a still-unsaved line's synthetic `new-<ts>` id is
          // replaced by the real one when the silent auto-save refetches — a
          // collapse recorded in that window would be orphaned and the item would
          // silently re-expand under the customer.
          onToggleCollapsed={canUpload ? onToggleCollapsed : undefined}
          bodyId={bodyId}
        />
        {collapsed && (
          // A collapsed item can still be the one holding a blocking miss, so
          // the flag says so — the inline red field it refers to is hidden.
          <RowRecap
            text={t('edit.collapse.recap', { quantity: draftQuantity(draft) })}
            flag={
              missCount > 0 ? t('edit.collapse.needsAttention', { count: missCount }) : undefined
            }
            bodyId={bodyId}
            editLabel={t('edit.collapse.edit')}
            onExpand={onToggleCollapsed}
          />
        )}
        {/* Hidden, never unmounted. SchemaForm holds per-line state in refs that
            nothing re-derives — the detected content-page baseline the sides
            toggle recomputes from, which fields the customer has touched, the
            "we filled this from your artwork" confirmations, the binding
            auto-switch notice. An unmount drops all of it silently and the
            customer's spec keeps the values with none of the machinery behind
            them. `hidden` also takes the whole subtree out of the tab order and
            the accessibility tree, which display:none alone would not. */}
        <div id={bodyId} hidden={collapsed}>
        {/* Per-project name. REQUIRED — judged by the one config validator
            (lineConfigIssues, handed the title alongside the config), so Save,
            the price pre-flight and the backend gate all flag it together.
            Blank still falls back to the live-derived `derivedName` everywhere
            the project is referenced, for the orders that predate the rule and
            for the headings that render while the customer is still typing.
            Dropped entirely once the order is past editing: the heading above
            already renders this exact value, live — so a locked copy of it would
            be the same answer twice, under a hint asking for an edit the surface
            won't take. */}
        {isEditable && (
        <div className="form-row">
          {/* Numbered so a multi-project quote doesn't present N identical
              "Item name" labels to assistive tech (mirrors the visible
              "Item N" eyebrow above). */}
          <div className="field-label-line">
            <label htmlFor={titleFieldId}>
              {t('edit.projectNameLabel', { n: index + 1 })}
              <span className="required-marker"> *</span>
            </label>
          </div>
          <input
            id={titleFieldId}
            type="text"
            // The live-derived spec name is offered as an EXAMPLE, never bare:
            // rendered bare it reads as an answer already given — the same text
            // the heading above shows — and the customer has no reason to type
            // into a required field that looks filled.
            placeholder={
              derivedName
                ? t('edit.projectNameSuggestion', { name: derivedName })
                : t('edit.projectNamePlaceholder')
            }
            value={draft.title}
            aria-invalid={titleError ? true : undefined}
            aria-describedby={titleError ? titleErrorId : undefined}
            onChange={(e) => onPatch({ title: e.target.value })}
          />
          <span className="help">
            {t('edit.projectNameHint')}
          </span>
          {titleError && (
            <span className="field-error" id={titleErrorId}>
              {titleError}
            </span>
          )}
        </div>
        )}
        {/* Unmapped historical product. When a past order's line couldn't be
            matched to a current product family, the product picker below is
            blank and none of the tiles reproduce what they ordered. Surface WHAT
            it was (the imported description, carried on the title) so they
            aren't guessing, and point a kitting-style job at the Assembly tile —
            the one assembly flow (ADR-020). This is context + guidance only —
            the portal makes no product decision. */}
        {isEditable && isHistorical && !draft.config.product_type && (
          <div className="banner banner--info" style={{ marginBottom: 16 }}>
            <strong>{t('edit.unmatchedProduct.title')}</strong>
            {draft.title ? (
              <p style={{ margin: '8px 0 0' }}>
                <Trans
                  i18nKey="edit.unmatchedProduct.originallyOrdered"
                  t={t}
                  values={{ title: draft.title }}
                  components={[<em key="title" />]}
                />
              </p>
            ) : null}
            <p style={{ margin: '8px 0 0' }}>
              {t('edit.unmatchedProduct.pickClosest')}{' '}
              {/kitting|assembly|\bkit\b/i.test(draft.title)
                ? `${t('edit.assemblyMovedNote')} `
                : ''}
              {t('edit.unmatchedProduct.notSureFits')}
            </p>
          </div>
        )}
        <SchemaForm
          schema={schema}
          values={draft.config}
          onChange={(next) => onPatch({ config: next })}
          errors={lineErrors}
          ruleErrorRevealSeq={ruleErrorRevealSeq}
          // Namespaces every field's DOM id by this project's local_id so the
          // missing-field summary can scroll to THIS project's copy (N projects
          // share one schema, so bare ids would collide — see lineFieldKey).
          fieldIdPrefix={draft.local_id}
          // Employee/impersonation context (ADR-008) — bypasses the min-100
          // quantity floor so an admin can place a sub-100 order.
          adminOverride={impersonating}
          // Cover/guts + hardcover endsheet/embossing uploads render INLINE in
          // their sections, sharing this line's asset state with the block below.
          lineAssets={lineAssets}
          canUpload={canUpload}
          // USB / disc content intake talks to its endpoints by line id, so it
          // waits for the persisted id just as the upload slots do.
          mediaContentScope={
            canUpload
              ? {
                  quote_id: quoteId,
                  quote_line_id: draft.local_id,
                  persist_upload_choice: onPersistMediaUploadChoice,
                }
              : undefined
          }
          // Locks the inline upload slots when the quote isn't editable (paid /
          // cancelled / rep-owned) — mirrors the residual block's `disabled`.
          disabled={!isEditable}
          // Past editing, every spec field renders as its ANSWER instead of a
          // control. `disabled` above reaches only the upload slots, and this card
          // sits in no fieldset, so without this a submitted order's radios,
          // swatches and text inputs still took keystrokes the PATCH would refuse.
          // Not `disabled` on the fields themselves: a natively disabled radio
          // group is out of the tab order, taking the customer's answer with it —
          // the same reason the Assembly panel reads back rather than greys out
          // (ADR-018 phase 13/14). readOnly implies inert inside SchemaForm.
          readOnly={!isEditable}
          // Drops the quote-level Need-By card after the Proofs section. The
          // closure is handed to every line and returns null for all but the
          // first — the quote holds one date, so it renders once.
          renderAfterGroup={renderAfterGroup}
          // Unlocks the dust-jacket product preset only when another project on
          // the quote is a hardbound book (we don't print dust jackets alone).
          // Narrows which product cards the picker offers to the ones this
          // quote's entry door offers. Never gates a stored value.
          entryDivisionKey={entryDivisionKey}
          // Drives the inline barcode advisory's "required for fulfillment" vs
          // "optional" copy (quote-level fulfillment signal).
          barcodeRequired={barcodeRequired}
          // Each inline slot's own "quote now, art to follow" checkbox.
          artworkPendingToggle={artworkPendingToggle}
          // A book's inserts point at the order's other saved projects — the
          // same list this item's own kit rows are offered below.
          insertLines={kitLines}
          hasUnsavedLines={hasUnsavedLines}
          lockedFields={
            assemblyItemIsPerUnit(draft.config)
              ? { quantity: { note: t('orderServices.assembly.perUnit.quantityLockedNote') } }
              : undefined
          }
        />

        {/* An assembly item authors its kit here, bound to its own snapshot at
            config.assembly — the one assembly flow (ADR-020). One mount per
            item, each over its own array, so the single-mount rule ADR-018
            Decision 2 protects (two "Add row" affordances over one list) holds
            by construction. How many kits is this item's own Quantity field
            (ADR-019), so the panel asks no count.

            Rendered on a submitted order too, `disabled`: the panel then reads
            the recipe back as static text, and this is the only place the
            customer can read the contents and build steps they authored — the
            SpecSummary rail beside it recaps the kit, it does not list it. */}
        {isAssemblyLine(draft) && (
          <AssemblyPanel
            quote_id={quoteId}
            photos={assemblyPhotos}
            schema={schema}
            value={itemAssembly ?? {}}
            // Across per_unit the structural quantity is reset to the floor along
            // with the config's, or draftQuantity would fall back to the old unit
            // total and save it as kits (or a kit count as units).
            onChange={(next) =>
              onPatch({
                config: withKitPickReconciled(
                  draft.config,
                  withAssemblyItemSnapshot(draft.config, next)
                ),
                ...(assemblyItemSwitchCrossesPerUnit(draft.config, next) ? { quantity: 1 } : {}),
              })
            }
            lines={kitLines}
            hasUnsavedLines={hasUnsavedLines}
            onRequestSave={onRequestSave}
            // Line-namespaced on both axes, like every other per-line control:
            // the errors arrive stripped back to bare field keys (what the panel
            // reads), while `fieldIdPrefix` namespaces the ids it RENDERS so two
            // assembly items never put the same `assembly.*` id in the document
            // twice.
            errors={lineErrors}
            fieldIdPrefix={draft.local_id}
            disabled={!isEditable}
            headingLevel="h4"
          />
        )}

        {/* "Something else" is a hand-off, not a spec. SchemaForm renders the
            product row, the request and its three details, and Quantity — and
            nothing else — so this is what stands in for the spec sections, and
            there is no artwork block, because a request the order form does not
            capture has nothing to upload against. */}
        {isSomethingElseLine(draft) ? (
          <TechnologyHandoff context="item" headingLevel="h3" />
        ) : (
        <div className="line-editor__artwork">
          {canUpload ? (
            <ArtworkUploader
              lineAssets={lineAssets}
              family={lineProductType(draft)}
              heading={t('edit.artworkFor', { name: itemLabel })}
              disabled={!isEditable}
              // Roles already rendered inline (cover/guts/endsheet/emboss) —
              // de-duped out of this block so they never double up.
              inlineRoles={inlineUploadRoles(schema, draft.config)}
              // The two config escape hatches that retire a required component
              // (and hide its inline slot), so this block doesn't re-offer it.
              selfCover={draft.config.self_cover === true}
              combinedArtwork={draft.config.combined_artwork === true}
              // Offered-but-unticked "cover and inside pages in one file" toggle,
              // so the Hands-on Prep slot can point a one-PDF book back to it.
              combinedArtworkToggleLabel={
                draft.config.combined_artwork === true
                  ? null
                  : shownFieldLabel(schema, 'combined_artwork', draft.config)
              }
              // Dedicated per-operation slots (emboss / foil / personalization
              // data + the step-8 variable-data spreadsheet) revealed by this
              // line's config, from the schema's file_reference conditional_show.
              extraSlots={deriveUploadSlots(schema, draft.config)}
              // "required for fulfillment" vs "optional" barcode advisory copy.
              barcodeRequired={barcodeRequired}
              // Each residual slot's own "quote now, art to follow" checkbox.
              artworkPendingToggle={artworkPendingToggle}
            />
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>{t('edit.artworkFor', { name: itemLabel })}</h3>
              <p className="help">
                {t('edit.saveFirstArtworkNote')}
              </p>
            </>
          )}
        </div>
        )}
        </div>
        {footer && <div className="line-editor__footer">{footer}</div>}
      </div>
      <div className="configurator-layout__aside" hidden={collapsed}>
        <SpecSummary
          schema={schema}
          values={draft.config}
          title={draft.title || derivedName || quoteTitle}
          quantity={draftQuantity(draft)}
          artworkUrl={artworkUrl}
          {...(isAssemblyLine(draft) ? { assembly: itemAssembly ?? {} } : {})}
        />
      </div>
    </div>
  );
}
